import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { GLAccountCombobox } from '../components/common/GLAccountSelector';
import { getCsrfToken } from '../utils/apiClient';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ContactEditModal from '../components/contacts/ContactEditModal';  // iter971k-Z · Turn 4
import { ContactTaxProfilePrompt, inferEntityTypeFromName } from '../components/contacts/ContactTaxProfilePrompt';
import EditBillStatusBanner from '../components/bill/EditBillStatusBanner';  // iter971k-Z · Turn 5
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import NewAccountModal from '../components/accounting/NewAccountModal';
import { FormattedNumberInput } from '../components/ui/formatted-number-input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { CurrencyInput, formatDisplayNumber } from '../components/ui/currency-input';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';
import { ProjectSelect } from '../components/projects/ProjectSelect';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, CalendarIcon, Loader2, Upload, FileText, X, Save, 
  CreditCard, CheckCircle2, Building, ExternalLink, AlertCircle, Scissors,
  Sparkles, Search, Clock, Package, Plus, Banknote, Trash2, Wand2, User, Brain, PenLine, ChevronDown,
  Edit3, Lightbulb, Lock,
} from 'lucide-react';
import AddBankAccountModal from '../components/AddBankAccountModal';
import { AISmartInput } from '../components/ai';
import FXRatePicker from '../components/common/FXRatePicker';
import SpecialItemModal, { getSpecialItemType } from '../components/SpecialItemModal';
import { AccountingStartDateModal } from '../components/shared/AccountingStartDateModal';
import { useDuplicateCheck } from '../components/DuplicateWarningDialog';
import { usePeriodLockCheck } from '../hooks/usePeriodLockCheck';
import { PeriodLockWarningDialog } from '../components/bankfeed/PeriodLockWarningDialog';
import { useTaxGateOpen } from '../utils/taxGateState';
import { BankTxActions } from '../components/bankfeed/BankTxActions';
import { useGlConcepts } from '../utils/glSemantic';
import { rankGlAccounts } from '../components/common/glAccountMeta';

// Line Item Row Component - CONNECTED TO CHART OF ACCOUNTS
const LineItemRow = ({ 
  item, 
  index, 
  updateLineItem, 
  removeLineItem, 
  lineItemsCount,
  getCurrencySymbol: getCurrencySymbolProp,  // Renamed to avoid collision with imported function
  searchCategory,
  loadingSuggestions,
  categorySuggestions,
  setIsAsset,
  setAssetType,
  setAssetUsefulLife,
  setShowAssetDialog,
  setIsPrepaidExpense,
  taxConfig,
  getTaxOptions,
  calculateLineTax,
  formatDisplayNumber,
  suggestedTaxes,
  applySuggestedTax,
  business,
  coaAccounts,
  isAmountLocked = false,
  onSpecialItemDetected,
  onAmountBlur,
  onRequestCreateAccount,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSelectedAccount, setAiSelectedAccount] = useState(null); // The AI's suggested account
  const [aiLowConfidence, setAiLowConfidence] = useState(false); // iter 757: flag ambiguous AI picks
  const dropdownRef = useRef(null);
  const lastSuggestedDescriptionRef = useRef(''); // Track last description that triggered AI suggestion
  const aiSuggestionTimerRef = useRef(null); // Timer ref for proper cleanup
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // AI Auto-suggest when description changes - with proper debouncing
  // Triggers when: 1) User stops typing for 1 second AND 2) Description changed significantly
  useEffect(() => {
    // Clear any existing timer when description changes
    if (aiSuggestionTimerRef.current) {
      clearTimeout(aiSuggestionTimerRef.current);
    }
    
    const currentDescription = item.description?.trim() || '';
    const lastSuggestedDescription = lastSuggestedDescriptionRef.current;
    
    // Only proceed if description is at least 3 characters
    if (currentDescription.length < 3) {
      return;
    }
    
    // Skip AI if GL code is already set AND it was manually selected (not from AI or bank feed)
    // Allow re-suggestion when: description changes significantly even if GL was AI-selected
    if (item.glCode && item.glCode.length > 0 && item.manuallySelected) {
      return;
    }
    
    // Check if description has changed significantly from last suggestion
    // "Significantly" means: different words OR description changed by more than 3 characters
    const hasChangedSignificantly = () => {
      if (!lastSuggestedDescription) return true; // Never suggested before
      if (currentDescription === lastSuggestedDescription) return false; // Exact same
      
      // Different first word = significant change
      const currentFirstWord = currentDescription.split(' ')[0].toLowerCase();
      const lastFirstWord = lastSuggestedDescription.split(' ')[0].toLowerCase();
      if (currentFirstWord !== lastFirstWord) return true;
      
      // If length changed by more than 5 characters, it's significant
      if (Math.abs(currentDescription.length - lastSuggestedDescription.length) > 5) return true;
      
      // Check if key words changed
      const currentWords = new Set(currentDescription.toLowerCase().split(/\s+/));
      const lastWords = new Set(lastSuggestedDescription.toLowerCase().split(/\s+/));
      const commonWords = [...currentWords].filter(w => lastWords.has(w));
      // If less than 50% of words are common, it's significant
      if (commonWords.length < Math.min(currentWords.size, lastWords.size) * 0.5) return true;
      
      return false;
    };
    
    // Only fetch AI suggestion if description changed significantly
    if (!hasChangedSignificantly()) {
      return;
    }

    // Iter 815 - If this line item came pre-locked from the Asset/Prepayment
    // schedule page, skip the AI auto-select entirely. The schedule's GL +
    // asset terms are authoritative; AI must not override them.
    if (item.presetFromSchedule) {
      return;
    }
    
    // Set a 1000ms debounce timer - waits for user to pause typing
    aiSuggestionTimerRef.current = setTimeout(async () => {
      setAiSuggesting(true);
      try {
        const response = await axios.post(`${API}/bills/suggest-category`, {
          query: currentDescription,
          country: business?.country
        });
        
        // Double-check description hasn't changed during the API call
        if (item.description?.trim() !== currentDescription) {
          console.log('[LineItemRow] Description changed during API call, skipping suggestion');
          return;
        }
        
        if (response.data?.suggestions && response.data.suggestions.length > 0) {
          const topSuggestion = response.data.suggestions[0];
          // iter 757 - surface low-confidence picks instead of silently auto-selecting
          // a generic bucket. Confidence < 0.6 = show review badge.
          const isLowConf = (topSuggestion.confidence ?? 0) < 0.6;
          setAiLowConfidence(isLowConf && !!topSuggestion.gl_code);
          if (topSuggestion.confidence > 0.5 && topSuggestion.gl_code) {
            // Find the account in COA
            const matchedAccount = coaAccounts.find(acc => acc.code === topSuggestion.gl_code);
            if (matchedAccount) {
              // Store the AI suggestion
              setAiSelectedAccount(matchedAccount);
              // AUTO-SELECT the account (this actually sets the GL code)
              selectAccount(matchedAccount, true); // true = AI selected
              // Remember this description as the one we suggested for
              lastSuggestedDescriptionRef.current = currentDescription;
              console.log('[LineItemRow] AI AUTO-SELECTED:', matchedAccount.name, 'GL:', matchedAccount.code, 'for:', currentDescription);
            }
          }
        }
      } catch (error) {
        console.log('AI suggestion error:', error);
      } finally {
        setAiSuggesting(false);
      }
    }, 1000); // 1000ms debounce - wait for user to finish typing before calling AI
    
    // Cleanup timer on unmount or when description changes
    return () => {
      if (aiSuggestionTimerRef.current) {
        clearTimeout(aiSuggestionTimerRef.current);
      }
    };
  }, [item.description, business?.country, coaAccounts]);
  
  // Select an account from COA - THIS ACTUALLY SETS THE GL CODE
  const selectAccount = (account, isAiSelection = false) => {
    // Actually set the values in the line item
    updateLineItem(item.id, 'category', account.name);
    updateLineItem(item.id, 'glCode', account.code);
    
    // Detect special item type from GL code
    const specialType = getSpecialItemType(account.code, coaAccounts);
    
    if (specialType) {
      // Mark as asset/special item
      updateLineItem(item.id, 'isAsset', true);
      updateLineItem(item.id, 'assetType', specialType);
      
      // Trigger special item modal via callback
      if (onSpecialItemDetected) {
        onSpecialItemDetected(item.id, specialType, account);
      }
    } else {
      updateLineItem(item.id, 'isAsset', false);
      updateLineItem(item.id, 'assetType', '');
    }
    
    if (isAiSelection) {
      setAiSelectedAccount(account);
      updateLineItem(item.id, 'manuallySelected', false);
    } else {
      // If user manually selected, clear AI suggestion and remember this description
      setAiSelectedAccount(null);
      updateLineItem(item.id, 'manuallySelected', true);
      // Iter 815 - manual selection releases the schedule-preset lock so
      // future AI changes can re-classify if the user keeps editing.
      updateLineItem(item.id, 'presetFromSchedule', false);
      // Track that we've already handled this description (prevents AI from overriding)
      lastSuggestedDescriptionRef.current = item.description?.trim() || '';
    }
    
    setSearchTerm('');
    setShowDropdown(false);
    
    console.log('[LineItemRow] SELECTED account:', account.name, 'GL:', account.code, 'AI:', isAiSelection, 'SpecialType:', specialType);
  };
  
  // Clear selection (uncheck) - hard reset, wipes GL + AI state entirely.
  const clearSelection = () => {
    updateLineItem(item.id, 'category', '');
    updateLineItem(item.id, 'glCode', '');
    updateLineItem(item.id, 'isAsset', false);
    updateLineItem(item.id, 'assetType', '');
    updateLineItem(item.id, 'manuallySelected', false);
    // Iter 815 - releasing the schedule-preset lock when user explicitly
    // clears the selection. From this point, AI may suggest a new GL.
    updateLineItem(item.id, 'presetFromSchedule', false);
    setAiSelectedAccount(null);
    // Reset the last suggested description so AI can re-suggest
    lastSuggestedDescriptionRef.current = '';
  };

  // Confirm the currently-selected account (iter971k-M · UX fix).
  // Users interpret the check-icon on the suggestion strip as "Confirm this
  // pick" - the old wiring called clearSelection() which nuked the pick and
  // forced a manual GL search. Now clicking "Confirm" flags the line as
  // user-confirmed (manuallySelected=true) so subsequent AI re-suggestions
  // won't override, and remembers the description so the AI won't reprocess.
  const confirmSelection = () => {
    if (!selectedAccount) return;
    updateLineItem(item.id, 'manuallySelected', true);
    lastSuggestedDescriptionRef.current = item.description?.trim() || '';
  };
  
  // Filter COA accounts based on search
  // Text match + meaning ("generator" → Office Equipment), relevance-ordered
  const glConcepts = useGlConcepts();
  const rankedGl = searchTerm.trim() ? rankGlAccounts({ query: searchTerm, accounts: coaAccounts, direction: 'outflow', topN: 3, concepts: glConcepts }) : null;
  const filteredAccounts = rankedGl ? rankedGl.groups.flatMap((g) => g.accounts) : coaAccounts;
  
  // Get the currently selected account object
  const selectedAccount = item.glCode ? coaAccounts.find(acc => acc.code === item.glCode) : null;
  const isAiSelected = aiSelectedAccount && item.glCode === aiSelectedAccount.code;

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="relative"
    >
      {/* Main Item Row - responsive: stacked on mobile, grid on desktop */}
      <div className="md:grid md:grid-cols-12 md:gap-3 md:items-start p-3 bg-muted/30 rounded-t-lg hover:bg-muted/50 transition-colors space-y-3 md:space-y-0">
        {/* Description */}
        <div className="md:col-span-4">
          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Description</label>
          <div className="relative">
            <Input
              placeholder="What is this expense for?"
              value={item.description}
              onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
              className={`h-11 text-base ${aiSuggesting ? 'pr-8' : ''}`}
              data-testid={`line-item-desc-${index}`}
            />
            {aiSuggesting && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[#108981]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              </div>
            )}
          </div>
          {aiLowConfidence && !aiSuggesting && (
            <div
              className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200"
              data-testid={`line-item-low-conf-badge-${index}`}
            >
              <span className="text-amber-500">!</span>
              Low confidence: please review the GL pick
            </div>
          )}
          {/* Per-line project allocation - overrides the bill-level project for this line */}
          <div className="mt-1.5 flex items-center gap-2" data-testid={`line-item-project-row-${index}`}>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">Project</span>
            <ProjectSelect
              compact
              value={item.projectId || null}
              onChange={(pid) => updateLineItem(item.id, 'projectId', pid)}
              createdFrom="bill_line"
              className="flex-1 min-w-0"
            />
          </div>
        </div>
        
        {/* Category / GL Dropdown Button */}
        <div className="md:col-span-2" ref={dropdownRef}>
          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Category / GL</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              className={`w-full h-11 px-3 rounded border flex items-center justify-between text-sm transition-colors ${
                selectedAccount 
                  ? 'bg-[#108981]/5 border-[#108981]/30 text-foreground hover:bg-[#108981]/10' 
                  : 'border-input bg-background text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`line-item-category-${index}`}
            >
              <span className="flex items-center gap-2 truncate">
                <Search className="w-4 h-4 flex-shrink-0" />
                {selectedAccount ? (
                  <span className="truncate">{selectedAccount.name}</span>
                ) : (
                  'Select GL Account...'
                )}
              </span>
              <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {/* COA Dropdown */}
            {showDropdown && (
              <div className="absolute left-0 top-full z-[60] mt-1 w-[340px] bg-card border border-border rounded-lg shadow-lg">
                {/* Search input */}
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-9 pl-8 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                
                {/* Account list with checkmarks */}
                <div className="max-h-[280px] overflow-auto">
                  {filteredAccounts.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                      No accounts found
                    </div>
                  ) : (
                    filteredAccounts.map((account) => {
                      const isSelected = item.glCode === account.code;
                      const isAiPick = aiSelectedAccount?.code === account.code;
                      return (
                        <button
                          key={account.code}
                          type="button"
                          onClick={() => selectAccount(account, false)}
                          className={`w-full px-3 py-2.5 flex items-center gap-2 hover:bg-muted transition-colors text-left text-sm border-b border-border/50 last:border-0 ${
                            isSelected ? 'bg-[#108981]/10' : ''
                          }`}
                        >
                          {/* Checkbox/Tick */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected 
                              ? 'bg-[#108981] border-[#108981]' 
                              : 'border-muted-foreground/30'
                          }`}>
                            {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                          </div>
                          
                          {/* Account info */}
                          <span className="font-mono text-xs text-muted-foreground w-12 flex-shrink-0">{account.code}</span>
                          <span className="truncate flex-1">{account.name}</span>
                          
                          {rankedGl?.meaning?.get(account.code) && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 font-medium flex-shrink-0" data-testid="gl-by-meaning" title={`“${rankedGl.meaning.get(account.code).phrase}” usually means ${rankedGl.meaning.get(account.code).concept}`}>
                              By meaning
                            </span>
                          )}
                          {/* Asset badge */}
                          {account.type === 'asset' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium flex-shrink-0">
                              Asset
                            </span>
                          )}
                          
                          {/* AI Pick badge */}
                          {isAiPick && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#108981]/10 text-[#108981] font-medium flex items-center gap-0.5 flex-shrink-0">
                              <Sparkles className="w-2.5 h-2.5" /> AI
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                  {/* Create New Account button */}
                  <button
                    type="button"
                    onClick={() => { setShowDropdown(false); onRequestCreateAccount?.(); }}
                    className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-[#108981]/10 text-[#108981] text-sm font-medium border-t border-border sticky bottom-0 bg-card"
                    data-testid="create-new-account-from-dropdown"
                  >
                    <Plus className="w-4 h-4" />
                    Create New Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Tax and Amount row - side by side on mobile */}
        <div className="grid grid-cols-2 gap-2 md:contents">
        {/* Tax Dropdown */}
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground md:hidden mb-1 block">VAT</label>
          <Select
            value={item.taxCode || 'NONE'}
            onValueChange={(value) => {
              const selectedTax = getTaxOptions().find(t => t.code === value);
              updateLineItem(item.id, 'taxCode', value);
              updateLineItem(item.id, 'taxRate', selectedTax?.rate || 0);
            }}
          >
            <SelectTrigger className={`h-11 text-sm ${suggestedTaxes?.[item.id] && (!item.taxCode || item.taxCode === 'NONE') ? 'ring-2 ring-[#108981]/50 border-[#108981]' : ''}`}>
              <SelectValue placeholder="VAT">
                {item.taxRate > 0 ? `${item.taxRate}%` : 'No VAT'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* AI Suggestion Header */}
              {suggestedTaxes?.[item.id] && (!item.taxCode || item.taxCode === 'NONE') && taxConfig?.taxes?.find(t => t.code === suggestedTaxes[item.id]) && (
                <div className="px-2 py-1.5 text-xs border-b border-border mb-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      applySuggestedTax(item.id);
                    }}
                    className="flex items-center gap-1 text-[#108981] hover:text-[#108981]/80 font-medium w-full"
                  >
                    <Wand2 className="w-3 h-3" />
                    AI Suggests: {taxConfig?.taxes?.find(t => t.code === suggestedTaxes[item.id])?.name}
                  </button>
                </div>
              )}
              {getTaxOptions().map((tax) => (
                <SelectItem key={tax.code} value={tax.code}>
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate flex items-center gap-1">
                      {tax.code === suggestedTaxes?.[item.id] && <Sparkles className="w-3 h-3 text-[#108981]" />}
                      {tax.name}
                    </span>
                    <span className="text-muted-foreground">{tax.rate}%</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* AI suggestion quick-apply button */}
          {suggestedTaxes?.[item.id] && (!item.taxCode || item.taxCode === 'NONE') && taxConfig?.taxes?.find(t => t.code === suggestedTaxes[item.id]) && (
            <button
              type="button"
              onClick={() => applySuggestedTax(item.id)}
              className="absolute -right-5 top-1/2 -translate-y-1/2 text-[#108981] hover:text-[#108981]/80"
              title="Apply AI suggestion"
            >
              <Wand2 className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Amount (with tax info) */}
        <div className="md:col-span-3">
          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Amount</label>
          <div className="flex flex-col">
            <div onBlur={() => onAmountBlur && onAmountBlur(item.id)}>
              <CurrencyInput
                value={item.amount}
                onChange={(value) => updateLineItem(item.id, 'amount', value)}
                currencySymbol={getCurrencySymbolProp()}
                placeholder="0.00"
                className={`h-11 text-base text-right ${isAmountLocked ? 'bg-muted cursor-not-allowed' : ''}`}
                disabled={isAmountLocked}
                data-testid={`line-item-amount-${index}`}
              />
            </div>
            {isAmountLocked && (
              <span className="text-xs text-amber-600 text-right mt-0.5">
                Amount locked (use Split to change)
              </span>
            )}
            {item.taxRate > 0 && !isAmountLocked && (
              <span className="text-xs text-muted-foreground text-right mt-0.5 whitespace-nowrap">
                incl. {getCurrencySymbolProp()}{formatDisplayNumber(calculateLineTax(item))} VAT
              </span>
            )}
          </div>
        </div>
        </div>{/* end Tax/Amount mobile grid */}
        
        {/* Delete */}
        <div className="md:col-span-1 flex items-center justify-center md:justify-center justify-end">
          {lineItemsCount > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => removeLineItem(item.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* SELECTED GL DISPLAY - iter971k-M · UX fix
           Two clear actions: (1) big obvious "Confirm" button on the left
           that locks the AI's pick, and (2) discreet ✕ on the far right
           to unselect. The old single check-icon called clearSelection()
           which users misread as "confirm" and inadvertently wiped their
           GL pick: forcing them to re-search manually. */}
      {selectedAccount && (() => {
        const isAsset = selectedAccount.type === 'asset';
        const isConfirmed = !!item.manuallySelected;
        const pendingAi = isAiSelected && !isConfirmed;
        const stripBg = isAsset
          ? 'bg-amber-50 border border-amber-200 border-t-amber-100'
          : 'bg-[#108981]/5 border border-[#108981]/20 border-t-[#108981]/10';
        return (
          <div className={`mx-3 mb-3 p-3 rounded-b-lg border-t-0 -mt-px flex items-center gap-x-3 gap-y-2 flex-wrap sm:flex-nowrap ${stripBg}`}>
            {/* GL badge + account name: full-width row on mobile so the name
                is always readable; inline on desktop. */}
            <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 min-w-0 order-1 sm:order-2">
              <div className={`px-2.5 py-1 rounded font-mono text-sm font-bold flex-shrink-0 ${
                isAsset ? 'bg-amber-200 text-amber-800' : 'bg-[#108981]/20 text-[#108981]'
              }`}>
                {item.glCode}
              </div>
              <span className="font-medium text-sm truncate min-w-0" title={selectedAccount.name}>{selectedAccount.name}</span>
            </div>
            {/* Primary action: Confirm the pick (locks it in). Pulses gently
                while pending; turns solid green with "Confirmed" once clicked. */}
            <button
              type="button"
              onClick={confirmSelection}
              disabled={isConfirmed}
              data-testid={`bill-gl-confirm-${item.id}`}
              aria-label={isConfirmed ? 'GL selection confirmed' : 'Confirm this GL selection'}
              className={`relative inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full text-xs font-semibold shadow-sm transition-all flex-shrink-0 order-2 sm:order-1 ${
                isConfirmed
                  ? 'bg-emerald-600 text-white cursor-default'
                  : (isAsset
                      ? 'bg-amber-500 hover:bg-amber-600 text-white ring-2 ring-amber-300 ring-offset-1 hover:scale-[1.03]'
                      : 'bg-[#108981] hover:bg-[#0d746d] text-white ring-2 ring-[#108981]/40 ring-offset-1 hover:scale-[1.03]')
              }`}
            >
              {pendingAi && (
                <span
                  className={`absolute inset-0 rounded-full pointer-events-none animate-ping opacity-60 ${
                    isAsset ? 'bg-amber-400' : 'bg-[#108981]/60'
                  }`}
                  style={{ animationDuration: '2.2s' }}
                  aria-hidden
                />
              )}
              <CheckCircle2 className="relative w-4 h-4" />
              <span className="relative">
                {isConfirmed ? 'Confirmed' : 'Confirm'}
              </span>
            </button>

            {/* Status chip */}
            {isConfirmed ? (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0 order-3">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Confirmed</span>
              </div>
            ) : isAiSelected ? (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#108981]/10 text-[#108981] flex-shrink-0 order-3">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">AI suggested</span>
              </div>
            ) : null}

            {/* Asset indicator */}
            {isAsset && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-200 text-amber-800 flex-shrink-0 order-4">
                <Package className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Asset</span>
              </div>
            )}

            {/* Secondary actions: Change (open dropdown) + Unselect (×) */}
            <button
              type="button"
              onClick={() => setShowDropdown(true)}
              className="text-xs text-muted-foreground hover:text-foreground underline flex-shrink-0 order-5 ml-auto sm:ml-0"
              data-testid={`bill-gl-change-${item.id}`}
            >
              Change
            </button>
            <button
              type="button"
              onClick={clearSelection}
              aria-label="Unselect this GL"
              title="Unselect"
              data-testid={`bill-gl-unselect-${item.id}`}
              className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 transition-colors order-6"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })()}
    </motion.div>
  );
};

const CreateBillPage = ({
  embedded = false,
  prefillOverride = null,
  onComplete = null,
  onCancel = null,
  // iter971k-X · Edit-mode. When set, load this bill, hydrate state, and PUT on save.
  editBillId: editBillIdProp = null,
}) => {
  const { business } = useAuth();
  const navigateRouter = useNavigate();
  const taxGateOpen = useTaxGateOpen();
  const location = useLocation();
  const { id: routeBillId } = useParams();
  // iter971k-X · Only enable edit mode when NOT embedded
  const editBillId = !embedded ? (editBillIdProp || routeBillId || null) : null;
  const isEditMode = !!editBillId;

  // iter 1011 - reserve & display the next bill number when the form opens.
  // Backend counter is atomic, so simultaneous forms never share a number.
  useEffect(() => {
    if (isEditMode) return;
    let cancelled = false;
    axios.get(`${API}/bills/next-number`)
      .then((res) => {
        if (!cancelled && res.data?.number) {
          setFormData((prev) => (prev.billNumber ? prev : { ...prev, billNumber: res.data.number }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEditMode]);
  // Embedded mode: when the host (Bank-Feed dialog) intercepts /bills
  // routes, call onComplete instead of navigating.
  const navigate = embedded
    ? (path) => {
        if (typeof path === 'string' && (path.startsWith('/bills') || path.startsWith('/bank-feeds'))) {
          onComplete?.();
        }
      }
    : navigateRouter;
  const [searchParams] = useSearchParams();
  
  // Check if this is a Fixed Asset flow from URL params
  const isAssetFromUrl = searchParams.get('isAsset') === 'true';
  const assetNameFromUrl = searchParams.get('assetName') || '';
  const assetCategoryFromUrl = searchParams.get('assetCategory') || '';
  const assetCategoryLabelFromUrl = searchParams.get('assetCategoryLabel') || '';
  const assetGlCodeFromUrl = searchParams.get('assetGlCode') || '';
  const assetCostFromUrl = searchParams.get('assetCost') || '';
  const assetUsefulLifeFromUrl = searchParams.get('assetUsefulLife') || '';
  const salvageValueFromUrl = searchParams.get('salvageValue') || '0';
  const purchaseDateFromUrl = searchParams.get('purchaseDate') || '';
  const vendorNameFromUrl = searchParams.get('vendorName') || '';
  const descriptionFromUrl = searchParams.get('description') || '';
  
  // Check if this is a Prepaid Expense flow from URL params
  const isPrepaidFromUrl = searchParams.get('isPrepaid') === 'true';
  const prepaidDescriptionFromUrl = searchParams.get('prepaidDescription') || '';
  const prepaidTypeFromUrl = searchParams.get('prepaidType') || 'other';
  const prepaidTypeLabelFromUrl = searchParams.get('prepaidTypeLabel') || 'Prepaid Expense';
  const prepaidGlCodeFromUrl = searchParams.get('prepaidGlCode') || '1300';
  const expenseGlCodeFromUrl = searchParams.get('expenseGlCode') || '';
  const [prepaidExpenseGl, setPrepaidExpenseGl] = useState(expenseGlCodeFromUrl);
  const prepaidAmountFromUrl = searchParams.get('prepaidAmount') || '';
  const spreadMonthsFromUrl = searchParams.get('spreadMonths') || '12';
  const startDateFromUrl = searchParams.get('startDate') || '';
  const prepaidVendorFromUrl = searchParams.get('vendorName') || '';
  
  // Check if this is an Inventory Purchase flow from URL params
  const isInventoryFromUrl = searchParams.get('isInventory') === 'true';
  const inventoryGlCodeFromUrl = searchParams.get('inventoryGlCode') || '1200';
  
  const [loading, setLoading] = useState(false);
  const [saveAction, setSaveAction] = useState('save');
  const [billProjectId, setBillProjectId] = useState(null);  // iter 1003: project dimension

  const [billDate, setBillDate] = useState(() => {
    if (purchaseDateFromUrl) {
      return new Date(purchaseDateFromUrl);
    }
    if (startDateFromUrl) {
      return new Date(startDateFromUrl);
    }
    return new Date();
  });
  const [dueDate, setDueDate] = useState(() => {
    if (purchaseDateFromUrl) {
      return addDays(new Date(purchaseDateFromUrl), 7);
    }
    if (startDateFromUrl) {
      return addDays(new Date(startDateFromUrl), 7);
    }
    return addDays(new Date(), 7);
  });
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [showBankModal, setShowBankModal] = useState(false);
  const docInputRef = useRef(null);

  // CHART OF ACCOUNTS - Source of truth for GL selection
  const [coaAccounts, setCoaAccounts] = useState([]);
  const [loadingCOA, setLoadingCOA] = useState(true);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);

  // AI Mode state - Don't show AI mode for asset, prepaid, or inventory purchases
  const [showAIMode, setShowAIMode] = useState(!isAssetFromUrl && !isPrepaidFromUrl && !isInventoryFromUrl);

  // Tax configuration state
  const [taxConfig, setTaxConfig] = useState(null);
  const [suggestedTaxes, setSuggestedTaxes] = useState({}); // Map of lineItemId -> suggested tax code
  const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
  
  // VAT Mode state (inclusive = default for bills, user enters gross amount)
  // Exclusive: User enters face value, VAT added on top → Gross = Face + VAT
  // Inclusive: User enters gross, VAT is inside → Face = Gross / (1 + VAT Rate)
  const [vatMode, setVatMode] = useState('inclusive'); // 'exclusive' or 'inclusive'
  const [taxSetupStatus, setTaxSetupStatus] = useState(null);
  // No WHT regime in this country and the business is not a CIS/PSWT-type contractor → hide the WHT panel entirely
  // Tax setup drives defaults and suggestions only - the manual WHT control is always available (CIS, backup withholding, no-ABN, PSWT…).
  const whtRegimeApplies = true;
  // Tax setup → default price display for NEW bills (bank-feed prefills stay locked inclusive; edits keep the bill's mode)
  useEffect(() => {
    if (isEditMode || prefillOverride || location.state?.bankFeedPrefill) return;
    axios.get(`${API}/tax-setup/status`).then((r) => {
      setTaxSetupStatus(r.data || null);
      if (r.data?.complete && r.data?.price_display) setVatMode(r.data.price_display);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Category AI suggestion state
  const [categorySearch, setCategorySearch] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [matchedGLCode, setMatchedGLCode] = useState('');
  const [suggestedDescription, setSuggestedDescription] = useState('');

  // Asset detection state - pre-fill from URL params if coming from Fixed Assets page
  const [isAsset, setIsAsset] = useState(isAssetFromUrl || isPrepaidFromUrl);
  const [assetType, setAssetType] = useState(
    isAssetFromUrl ? 'depreciable' : 
    isPrepaidFromUrl ? 'amortizable_prepaid' : ''
  );
  
  // Accounting Start Date Modal state
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  
  // Duplicate transaction check
  const { checkForDuplicates, DuplicateDialog } = useDuplicateCheck();
  
  // Period lock check for bank feed transactions
  const { checkPeriodLock, periodLockDialog, clearPeriodLock } = usePeriodLockCheck();
  const periodLockOverrideRef = useRef(false);
  const periodLockOverrideDateRef = useRef(null);
  const pendingSubmitActionRef = useRef('save');
  
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [assetUsefulLife, setAssetUsefulLife] = useState(assetUsefulLifeFromUrl || spreadMonthsFromUrl);
  const [depreciationMethod, setDepreciationMethod] = useState('straight_line');
  
  // New: Depreciation/Amortization start date state
  const [depreciationStartOption, setDepreciationStartOption] = useState('this_month'); // 'this_month', 'next_month', 'custom'
  const [customStartDate, setCustomStartDate] = useState(''); // YYYY-MM format
  const [assetSetupCancelled, setAssetSetupCancelled] = useState(false); // Track if user cancelled asset setup
  
  // iter 817 Phase 8 - Recognition policy lock for FIXED ASSET useful-life.
  // Prepaid coverage is NOT locked (it's bill-driven per Decision 10).
  const [recognitionPolicy, setRecognitionPolicy] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/recognition-policy`)
      .then((res) => { if (!cancelled) setRecognitionPolicy(res.data?.active || null); })
      .catch(() => { /* lock is best-effort UX */ });
    return () => { cancelled = true; };
  }, []);

  // iter 817 Phase 8 - When the recognition policy + asset category from
  // URL are both known, FORCE the useful-life to the policy-locked value
  // (matching the backend lock in Phase 5).  This prevents stale URL
  // values from being saved when the user clicks through quickly.
  useEffect(() => {
    if (!recognitionPolicy?.depreciation?.useful_life_locked) return;
    if (!assetCategoryFromUrl) return;
    const lockedLife = recognitionPolicy.depreciation.useful_life_locked[assetCategoryFromUrl];
    if (lockedLife && lockedLife > 0) {
      setAssetUsefulLife(String(lockedLife));
    }
  }, [recognitionPolicy, assetCategoryFromUrl]);

  // Prepaid expense state - prepaid items cannot be "approved", they must be paid
  const [isPrepaidExpense, setIsPrepaidExpense] = useState(isPrepaidFromUrl);

  // iter971k-X · Edit-mode hydration for Bill. When the URL is /bills/:id/edit,
  // load the existing bill and populate every state variable so this same form
  // renders in Edit mode with full parity to Create. On save, PUT instead of POST.
  const [editLoaded, setEditLoaded] = useState(false);
  const [existingBill, setExistingBill] = useState(null);

  // iter971k-Z · Turn 5c - Freeze all financial fields when the bill has
  // recorded payments, is reconciled, or is written-off/cancelled.
  const financeLocked = Boolean(
    isEditMode && existingBill && (
      Number(existingBill.amount_paid || 0) > 0
      || ['paid', 'partial', 'partially_paid', 'written_off', 'cancelled', 'reconciled'].includes(String(existingBill.status || '').toLowerCase())
      || existingBill.is_reconciled
      || existingBill.reconciled
      || existingBill.reconciliation_id
      || existingBill.bank_feed_transaction_id
      || existingBill.bank_feed_tx_id
    )
  );
  const bankFeedLocked = Boolean(isEditMode && existingBill && (existingBill.bank_feed_transaction_id || existingBill.bank_feed_tx_id || existingBill.bank_feed_link_ids?.length || existingBill.bank_feed_source));
  const financeLockClass = financeLocked ? 'pointer-events-none opacity-70 select-none' : '';
  useEffect(() => {
    if (!isEditMode || editLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: bill } = await axios.get(`${API}/bills/${editBillId}`);
        if (cancelled) return;
        setExistingBill(bill);
        setBillProjectId(bill.project_id || null);
        setFormData({
          vendorName: bill.vendor_name || '',
          amount: bill.amount?.toString() || '',
          description: bill.description || '',
          category: bill.category || '',
          glCode: bill.gl_code || '',
          billNumber: bill.bill_number || '',
          vendorEntityType: bill.vendor_entity_type || '',
        });
        setCategorySearch(bill.category || '');
        setMatchedGLCode(bill.gl_code || '');
        setIsAsset(bill.is_asset || false);
        setAssetType(bill.asset_type || '');
        if (bill.bill_date) { try { setBillDate(parseISO(bill.bill_date)); } catch { /* ignore malformed date */ } }
        if (bill.due_date) { try { setDueDate(parseISO(bill.due_date)); } catch { /* ignore malformed date */ } }
        if (bill.pay_from_bank_id) setSelectedBankId(bill.pay_from_bank_id);
        if (bill.supporting_documents?.length > 0) {
          setSupportingDocs(bill.supporting_documents.map((docId, idx) => ({
            id: docId, filename: `Document ${idx + 1}`, file_type: 'pdf',
          })));
        }
        // Currency + FX
        if (bill.currency) setBillCurrency(bill.currency);
        if (bill.fx_rate != null) {
          try { setFxRate(Number(bill.fx_rate) || 1); } catch { /* ignore */ }
        }
        if (bill.fx_rate_source) setFxRateSource(bill.fx_rate_source);
        // iter971k-Z · Turn 5c - hydrate line items so duplicates show all
        // rows (previously only formData scaffolding loaded, leaving an
        // empty line item on the form).
        if (Array.isArray(bill.line_items) && bill.line_items.length > 0) {
          setLineItems(bill.line_items.map((li, idx) => ({
            id: idx + 1,
            description: li.description || '',
            category: li.category || bill.category || '',
            glCode: li.gl_code || li.expense_account_code || bill.gl_code || '',
            // amount is per-unit price when quantity>0, else full line amount
            amount: (li.unit_price ?? li.amount ?? li.line_total ?? '')?.toString?.() || '',
            quantity: li.quantity || 1,
            isAsset: li.is_asset || false,
            assetType: li.asset_type || '',
            // GL was chosen when the bill was saved - treat as confirmed so
            // the strip shows "Confirmed" and AI doesn't re-suggest/re-open
            // special-item modals on edit.
            manuallySelected: !!(li.gl_code || li.expense_account_code || bill.gl_code),
            taxCode: li.tax_code || 'NONE',
            taxRate: li.tax_rate || 0,
            productId: li.product_id || null,
            projectId: li.project_id || null,
            matchedProduct: li.product_id ? {
              id: li.product_id,
              name: li.product_name || li.description,
              revenue_gl_code: li.revenue_account_code,
            } : null,
          })));
        }
        setEditLoaded(true);
      } catch (err) {
        toast.error('Failed to load bill');
        navigateRouter('/bills');
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBillId, isEditMode]);

  // iter971k-Z · Turn 5d - Second-pass hydration that runs AFTER the state
  // hooks below are declared. It restores VAT mode, WHT rate, and asset
  // parameters so a duplicated bill lands on the edit view with every
  // tax/WHT/asset toggle already applied. Line-item level tax codes are
  // already hydrated in the primary useEffect.
  useEffect(() => {
    if (!isEditMode || !existingBill) return;
    const b = existingBill;
    if (b.vat_mode) setVatMode(b.vat_mode);
    if (b.apply_wht) {
      const rate = b.wht_rate_override ?? b.wht_rate ?? null;
      if (rate != null) setSelectedWhtRate(Number(rate) || 0);
    }
    if (b.asset_useful_life_months) setAssetUsefulLife(Number(b.asset_useful_life_months));
    if (b.depreciation_method) setDepreciationMethod(b.depreciation_method);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingBill, isEditMode]);

  // Payment confirmation dialog state
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const [savedBillId, setSavedBillId] = useState(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [completingPayment, setCompletingPayment] = useState(false);
  
  // Partial payment state
  const [showPartialPaymentDialog, setShowPartialPaymentDialog] = useState(false);
  const [partialPaymentAmount, setPartialPaymentAmount] = useState('');
  const [partialPaymentRef, setPartialPaymentRef] = useState('');
  const [partialPaymentDate, setPartialPaymentDate] = useState(new Date()); // iter971k-Z · Turn 5c
  // iter971k-Z · Turn 5c - Confirm-payment-date dialog for Mark Paid.
  const [showPaymentDateDialog, setShowPaymentDateDialog] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(null);
  const [confirmedPaymentDate, setConfirmedPaymentDate] = useState(new Date());
  
  // WHT-specific state
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  // iter959 - debounce vendor search HTTP calls
  const vendorSearchTimerRef = useRef(null);
  const [whtPreview, setWhtPreview] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [contactEditOpen, setContactEditOpen] = useState(false);  // iter971k-Z · Turn 4
  
  // AI WHT Suggestion state (Smart WHT Deduction feature)
  const [aiWhtSuggestion, setAiWhtSuggestion] = useState(null);
  const [fetchingWhtSuggestion, setFetchingWhtSuggestion] = useState(false);
  const [selectedWhtRate, setSelectedWhtRate] = useState(null); // User-confirmed WHT rate
  const whtDismissedRef = useRef(false); // user chose 'No WHT' → engine must not re-apply
  const selectedWhtRateRef = useRef(null);
  useEffect(() => { selectedWhtRateRef.current = selectedWhtRate; }, [selectedWhtRate]);
  const [showWhtOptions, setShowWhtOptions] = useState(false);
  
  // Unified AI Tax Suggestion state
  const [aiTaxSuggestion, setAiTaxSuggestion] = useState(null);
  const [fetchingUnifiedTax, setFetchingUnifiedTax] = useState(false);
  
  // Force bank account creation modal
  const [showForceBankModal, setShowForceBankModal] = useState(false);
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  
  // Bank Feed Transaction completion state
  const [bankFeedTransactionId, setBankFeedTransactionId] = useState(null);
  const [originalBankFeedStatus, setOriginalBankFeedStatus] = useState(null);
  const [isFromBankFeed, setIsFromBankFeed] = useState(false);
  
  // Split Transaction state
  const [isSplitMode, setIsSplitMode] = useState(false);
  const [splitExpectedTotal, setSplitExpectedTotal] = useState(0);
  
  // "Not Quite Right" mode state - locks amount and prevents adding new lines
  const [isNotQuiteRightMode, setIsNotQuiteRightMode] = useState(false);
  const [lockedAmount, setLockedAmount] = useState(0);
  
  // NEW: Special Item Modal State (Fixed Asset, Prepaid, Investment, Loan, Deferred Revenue)
  const [showSpecialItemModal, setShowSpecialItemModal] = useState(false);
  const [specialItemType, setSpecialItemType] = useState(null);
  const [currentSpecialItemLineId, setCurrentSpecialItemLineId] = useState(null);
  const [specialItemTerms, setSpecialItemTerms] = useState({});
  const [specialItemQueue, setSpecialItemQueue] = useState([]);
  const [pendingSpecialItems, setPendingSpecialItems] = useState({}); // { lineItemId: { type, account } } - detected but modal not yet shown
  
  // FX Rate state
  // Currency / FX / bank handed over from the Fixed Assets & Prepaid dashboards
  // (currency is derived from the bank account chosen there - keep them in sync).
  const currencyFromUrl = (searchParams.get('currency') || '').toUpperCase();
  const fxRateFromUrl = parseFloat(searchParams.get('fxRate') || '');
  const bankAccountIdFromUrl = searchParams.get('bankAccountId') || '';
  const [billCurrency, setBillCurrency] = useState(currencyFromUrl || null);
  const [fxRate, setFxRate] = useState(fxRateFromUrl > 0 ? fxRateFromUrl : 1);
  const [fxRateSource, setFxRateSource] = useState(fxRateFromUrl > 0 ? (searchParams.get('fxRateSource') || 'manual') : 'live');
  useEffect(() => {
    if (bankAccountIdFromUrl) setSelectedBankId(bankAccountIdFromUrl);
  }, [bankAccountIdFromUrl]);
  
  // Multi-line items state - pre-fill from URL params if coming from Fixed Assets, Prepaid Expenses, or Inventory
  const [lineItems, setLineItems] = useState(() => {
    if (isAssetFromUrl && assetNameFromUrl && assetCostFromUrl) {
      return [{
        id: 1,
        description: assetNameFromUrl,
        category: assetCategoryLabelFromUrl || assetCategoryFromUrl,
        glCode: assetGlCodeFromUrl,
        amount: assetCostFromUrl,
        isAsset: true,
        assetType: 'depreciable',
        taxCode: 'NONE',
        taxRate: 0,
        // Iter 815 - lock this line so the per-row AI suggestion does not
        // overwrite the GL / asset terms chosen on the schedule page.
        presetFromSchedule: true,
      }];
    }
    if (isPrepaidFromUrl && prepaidDescriptionFromUrl && prepaidAmountFromUrl) {
      return [{
        id: 1,
        description: prepaidDescriptionFromUrl,
        category: prepaidTypeLabelFromUrl,
        glCode: prepaidGlCodeFromUrl,
        amount: prepaidAmountFromUrl,
        isAsset: true,
        assetType: 'amortizable_prepaid',
        taxCode: 'NONE',
        taxRate: 0,
        // Iter 815 - same lock as fixed-asset path
        presetFromSchedule: true,
      }];
    }
    if (isInventoryFromUrl) {
      return [{
        id: 1,
        description: searchParams.get('inventoryDescription') || 'Inventory Purchase',
        category: 'Inventory',
        glCode: inventoryGlCodeFromUrl,
        amount: searchParams.get('inventoryAmount') || '',
        isAsset: false,
        assetType: '',
        taxCode: 'NONE',
        taxRate: 0
      }];
    }
    return [
      { id: 1, description: '', category: '', glCode: '', amount: '', isAsset: false, assetType: '', taxCode: 'NONE', taxRate: 0 }
    ];
  });
  
  const [formData, setFormData] = useState({
    vendorName: vendorNameFromUrl || prepaidVendorFromUrl || '',
    amount: assetCostFromUrl || prepaidAmountFromUrl || '',
    description: descriptionFromUrl || 
      (isAssetFromUrl ? `Fixed Asset Purchase: ${assetNameFromUrl}` : '') ||
      (isPrepaidFromUrl ? `Prepaid Expense: ${prepaidDescriptionFromUrl}` : ''),
    category: assetCategoryLabelFromUrl || prepaidTypeLabelFromUrl || '',
    glCode: assetGlCodeFromUrl || prepaidGlCodeFromUrl || '',
    billNumber: '',  // iter971k-W · user override. Blank = auto BILL-XXXXXXXX
    vendorEntityType: ''  // 'corporate' or 'individual'
  });
  
  // Get available tax options based on business country
  const getTaxOptions = () => {
    if (!taxConfig || !taxConfig.taxes) return [];
    // Per-line tax dropdown should ONLY show VAT options, NOT WHT
    // WHT is handled separately in the totals/WHT suggestion section
    const vatOnly = taxConfig.taxes.filter(t => t.tax_type !== 'withholding');
    return [
      { code: 'NONE', name: 'No VAT', rate: 0 },
      ...vatOnly
    ];
  };

  // Helper: Calculate depreciation/amortization start date based on 15th rule
  // If asset is recognized after 15th of the month, depreciation starts next month
  const calculateDepreciationStartDate = () => {
    const today = new Date();
    const dayOfMonth = today.getDate();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    let startMonth, startYear;
    
    if (depreciationStartOption === 'this_month') {
      // If recognized after 15th, automatically push to next month
      if (dayOfMonth > 15) {
        startMonth = currentMonth + 1;
        startYear = currentYear;
        if (startMonth > 11) {
          startMonth = 0;
          startYear++;
        }
      } else {
        startMonth = currentMonth;
        startYear = currentYear;
      }
    } else if (depreciationStartOption === 'next_month') {
      startMonth = currentMonth + 1;
      startYear = currentYear;
      if (startMonth > 11) {
        startMonth = 0;
        startYear++;
      }
    } else if (depreciationStartOption === 'custom' && customStartDate) {
      const [year, month] = customStartDate.split('-').map(Number);
      startYear = year;
      startMonth = month - 1; // JS months are 0-indexed
    } else {
      // Default to current month
      startMonth = currentMonth;
      startYear = currentYear;
    }
    
    // Return as YYYY-MM-25 (entries are posted on 25th of each month)
    return `${startYear}-${String(startMonth + 1).padStart(2, '0')}-25`;
  };

  // Helper: Get display text for start date
  const getStartDateDisplay = () => {
    const today = new Date();
    const dayOfMonth = today.getDate();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    if (depreciationStartOption === 'this_month') {
      if (dayOfMonth > 15) {
        // Automatically pushed to next month
        const nextMonth = (today.getMonth() + 1) % 12;
        const year = nextMonth === 0 ? today.getFullYear() + 1 : today.getFullYear();
        return `${months[nextMonth]} ${year} (auto-adjusted, recognized after 15th)`;
      }
      return `${months[today.getMonth()]} ${today.getFullYear()}`;
    } else if (depreciationStartOption === 'next_month') {
      const nextMonth = (today.getMonth() + 1) % 12;
      const year = nextMonth === 0 ? today.getFullYear() + 1 : today.getFullYear();
      return `${months[nextMonth]} ${year}`;
    } else if (depreciationStartOption === 'custom' && customStartDate) {
      const [year, month] = customStartDate.split('-').map(Number);
      return `${months[month - 1]} ${year}`;
    }
    return 'Select start date';
  };

  // Helper: Generate month options for custom date picker (past 3 months to future 12 months)
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Start from 3 months ago
    for (let i = -3; i <= 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = `${months[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value, label });
    }
    return options;
  };

  // Calculate line item tax amount
  // In EXCLUSIVE mode: Tax = entered amount * tax rate
  // In INCLUSIVE mode (default): Tax = entered amount - (entered amount / (1 + tax rate))
  const calculateLineTax = (item) => {
    const amount = parseFloat(item.amount) || 0;
    const taxRate = item.taxRate || 0;
    if (taxRate === 0) return 0;
    
    if (vatMode === 'inclusive') {
      // For inclusive VAT: VAT = Amount - (Amount / (1 + rate))
      return amount - (amount / (1 + taxRate / 100));
    } else {
      // For exclusive VAT: VAT = Amount * rate
      return amount * (taxRate / 100);
    }
  };

  // Calculate net amount (face value before tax) for a line item
  // In EXCLUSIVE mode: Face Value = entered amount
  // In INCLUSIVE mode (default): Face Value = entered amount / (1 + tax rate)
  const calculateLineNet = (item) => {
    const amount = parseFloat(item.amount) || 0;
    const taxRate = item.taxRate || 0;
    
    if (vatMode === 'inclusive') {
      if (taxRate === 0) return amount;
      // For inclusive VAT: Face Value = Amount / (1 + rate)
      return amount / (1 + taxRate / 100);
    } else {
      // For exclusive VAT: Face Value = Amount (tax is added on top)
      return amount;
    }
  };
  
  // Calculate line item total (what displays in TOTAL column)
  // In EXCLUSIVE mode: Total = entered amount + tax
  // In INCLUSIVE mode (default): Total = entered amount (tax is already inside)
  const calculateLineTotal = (item) => {
    const amount = parseFloat(item.amount) || 0;
    
    if (vatMode === 'inclusive') {
      // In inclusive mode, the entered amount IS the total
      return amount;
    } else {
      // In exclusive mode, add tax on top
      return amount + calculateLineTax(item);
    }
  };

  // Calculate subtotal (net amounts before tax)
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + calculateLineNet(item), 0);
  };

  // Calculate total tax
  const calculateTotalTax = () => {
    return lineItems.reduce((sum, item) => sum + calculateLineTax(item), 0);
  };
  
  // Line item functions
  const addLineItem = () => {
    const newId = Math.max(...lineItems.map(i => i.id), 0) + 1;
    const defaultTax = taxConfig?.taxes?.[0] || { code: 'NONE', rate: 0 };
    setLineItems([...lineItems, { 
      id: newId, description: '', category: '', glCode: '', amount: '', isAsset: false, assetType: '',
      taxCode: defaultTax.code || 'NONE', taxRate: defaultTax.rate || 0
    }]);
  };
  
  const removeLineItem = (id) => {
    setLineItems(prevItems => {
      if (prevItems.length <= 1) return prevItems;
      return prevItems.filter(item => item.id !== id);
    });
  };
  
  const updateLineItem = (id, field, value) => {
    setLineItems(prevItems => prevItems.map(item => 
      item.id === id ? { ...item, [field]: value, ...(field === 'taxCode' ? { taxAuto: false } : {}) } : item
    ));
  };
  
  // Fetch Chart of Accounts - Source of truth for GL selection
  const fetchChartOfAccounts = async () => {
    setLoadingCOA(true);
    try {
      const response = await axios.get(`${API}/accounting/chart-of-accounts`);
      const accounts = response.data?.accounts || response.data || [];
      // Filter to only expense and asset accounts for bills
      const relevantAccounts = accounts.filter(acc => 
        ['expense', 'asset'].includes(acc.type)
      ).sort((a, b) => a.code.localeCompare(b.code));
      setCoaAccounts(relevantAccounts);
      console.log('[CreateBillPage] Loaded', relevantAccounts.length, 'COA accounts');
    } catch (error) {
      console.error('Error fetching COA:', error);
      toast.error('Failed to load Chart of Accounts');
    } finally {
      setLoadingCOA(false);
    }
  };

  // Fetch bank registry for smart payment redirection
  const fetchBankRegistry = async () => {
    try {
      const response = await axios.get(`${API}/bank-registry`);
      setBankRegistry(response.data?.banks || []);
    } catch (error) {
      console.log('Bank registry not available, payment redirection will use fallback');
    }
  };

  
  // Regional second tax (CA PST/RST/QST) — only from the tax engine's decision for this business's province
  const secondaryTaxInfo = (aiTaxSuggestion?.suggestions || []).find((sg) => sg?.decision?.secondary_tax?.applies)?.decision?.secondary_tax || null;
  const calculateSecondaryTax = () => {
    if (!secondaryTaxInfo || isFromBankFeed) return 0;
    return Math.round(calculateSubtotal() * secondaryTaxInfo.rate) / 100;
  };

  const calculateTotal = () => {
    // Total (gross) = sum of line totals + any regional second tax
    return lineItems.reduce((sum, item) => sum + calculateLineTotal(item), 0) + calculateSecondaryTax();
  };

  // WHT Helper Functions
  const searchVendorSuggestions = async (query) => {
    if (query.length < 2) {
      setVendorSuggestions([]);
      return;
    }
    // iter959 - 300ms debounce so we don't fire an HTTP call on every keystroke.
    if (vendorSearchTimerRef.current) clearTimeout(vendorSearchTimerRef.current);
    vendorSearchTimerRef.current = setTimeout(async () => {
      try {
        // Search both WHT vendors and contacts
        const [whtResponse, contactsResponse] = await Promise.all([
          fetch(`${API}/wht/vendors/search/${encodeURIComponent(query)}`, {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          }),
          axios.get(`${API}/contacts/search`, {
            params: {
              q: query,
              contact_type: 'vendor'
            }
          })
        ]);

        let vendors = [];

        // Add WHT vendors
        if (whtResponse.ok) {
          const whtData = await whtResponse.json();
          vendors = [...(whtData.vendors || [])];
        }

        // Add contact vendors (avoiding duplicates by name)
        const contactVendors = contactsResponse.data?.contacts || [];
        const existingNames = new Set(vendors.map(v => v.name.toLowerCase()));

        contactVendors.forEach(contact => {
          if (!existingNames.has(contact.name.toLowerCase())) {
            vendors.push({
              id: contact.id,
              name: contact.name,
              entity_type: contact.tax_attributes?.entity_type || contact.entity_type || (contact.company_name ? 'corporate' : 'individual'),
              email: contact.email,
              phone: contact.phone,
              is_contact: true
            });
          }
        });

        setVendorSuggestions(vendors);
      } catch (error) {
        console.error('Error searching vendors:', error);
      }
    }, 300);
  };
  
  const selectVendor = (vendor) => {
    setFormData({ 
      ...formData, 
      vendorName: vendor.name,
      vendorEntityType: vendor.entity_type || (vendor.tax_attributes && vendor.tax_attributes.entity_type) || inferEntityTypeFromName(vendor.name)
    });
    setSelectedVendorId(vendor.id);
    setVendorSuggestions([]);
    
    // Calculate WHT preview
    if (calculateTotal() > 0) {
      calculateWHTPreview(vendor.entity_type);
    }
  };
  
  const calculateWHTPreview = async (entityType) => {
    const total = calculateTotal();
    if (!entityType || total <= 0) {
      setWhtPreview(null);
      return;
    }
    
    // Get category from first line item (or general)
    const category = lineItems[0]?.category || 'general';
    
    try {
      const response = await fetch(`${API}/wht/calculate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken() || '',
        },
        body: JSON.stringify({
          vendor_name: formData.vendorName || 'Test',
          entity_type: entityType,
          category: category,
          gross_amount: total
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setWhtPreview(data);
      }
    } catch (error) {
      console.error('Error calculating WHT:', error);
    }
  };
  
  // ========== UNIFIED AI TAX SUGGESTION ENGINE ==========
  // Replaces separate VAT and WHT suggestion flows with one intelligent call
  
  const fetchUnifiedTaxSuggestion = async () => {
    const hasVendor = formData.vendorName?.trim();
    const hasDescription = lineItems.some(item => (item.description || '').trim().length >= 3);
    if (!hasVendor && !hasDescription) return;
    
    setFetchingUnifiedTax(true);
    try {
      const requestItems = lineItems
        .filter(item => (item.description || '').length >= 2 || (item.category || '').length >= 2)
        .map(item => ({
          description: item.description || item.category || '',
          amount: parseFloat(item.amount) || 0,
          category: item.category || 'expenses'
        }));
      
      if (requestItems.length === 0) { setFetchingUnifiedTax(false); return; }
      
      const response = await axios.post(`${API}/tax-management/ai-suggest`, {
        transaction_type: 'bill',
        contact_id: selectedVendorId || null,
        vendor_name: formData.vendorName || '',
        vendor_entity_type: formData.vendorEntityType || 'corporate',
        is_international: formData.isInternational || false,
        items: requestItems
      });
      
      if (response.data) {
        setAiTaxSuggestion(response.data);
        
        // Populate per-line VAT suggestions
        const itemsWithData = lineItems.filter(item => 
          (item.description || '').length >= 2 || (item.category || '').length >= 2
        );
        const newSuggestions = {};
        const suggestions = response.data.suggestions || [];
        itemsWithData.forEach((item, idx) => {
          if (suggestions[idx]?.vat) {
            newSuggestions[item.id] = suggestions[idx].vat.code;
          }
        });
        setSuggestedTaxes(newSuggestions);
        // Tax engine is definitive → apply input VAT to untaxed lines automatically (user can still change it)
        setLineItems(prev => prev.map(item => {
          const idx = itemsWithData.findIndex(i => i.id === item.id);
          const s = idx >= 0 ? suggestions[idx] : null;
          const t = s?.vat && s?.decision?.definitive && !s?.review_required && taxConfig?.taxes?.find(x => x.code === s.vat.code);
          if (t && (!item.taxCode || item.taxCode === 'NONE')) return { ...item, taxCode: t.code, taxRate: t.rate, taxAuto: true };
          // Engine now says no input VAT (e.g. vendor switched to a reverse-charge / non-resident supplier): clear a code WE applied, never a user pick
          if (s && !s.vat && s?.decision?.definitive && item.taxAuto) return { ...item, taxCode: 'NONE', taxRate: 0, taxAuto: false };
          return item;
        }));
        
        // Populate WHT suggestion from first item (bill-level)
        const firstSuggestion = suggestions[0];
        if (firstSuggestion?.wht) {
          setAiWhtSuggestion({
            is_applicable: true,
            suggested_wht_rate: firstSuggestion.wht.rate,
            wht_amount: firstSuggestion.wht.tax_amount,
            suggested_entity_type: response.data.business_context?.vendor_entity_type || formData.vendorEntityType,
            reasoning: firstSuggestion.reasoning || '',
            confidence: 0.9
          });
          if (!selectedWhtRateRef.current) {
            setShowWhtOptions(true);
            // Engine is definitive → apply WHT automatically (same brain as bank-feed auto-post); user can still change it
            if (firstSuggestion.decision?.definitive && !firstSuggestion.review_required && !whtDismissedRef.current) {
              setSelectedWhtRate(firstSuggestion.wht.rate);
            }
          }
        } else if (firstSuggestion) {
          setAiWhtSuggestion({
            is_applicable: false,
            suggested_wht_rate: 0,
            reasoning: firstSuggestion.reasoning || 'WHT not applicable',
            confidence: 0.9
          });
        }
      }
    } catch (error) {
      console.log('Unified tax suggestion error:', error);
      // Fallback: try the old WHT endpoint
      try {
        const total = calculateTotal();
        if (total > 0) {
          const primaryItem = lineItems.find(item => item.description || item.category);
          const response = await axios.post(`${API}/wht/suggest`, {
            vendor_name: formData.vendorName || null,
            description: primaryItem?.description || null,
            category: primaryItem?.category || null,
            gross_amount: total,
            entity_type: formData.vendorEntityType || null
          });
          if (response.data) {
            setAiWhtSuggestion(response.data);
            if (response.data.suggested_wht_rate > 0 && response.data.confidence > 0.7 && !selectedWhtRate) {
              setShowWhtOptions(true);
            }
          }
        }
      } catch (_) { /* silent fallback */ }
    } finally {
      setFetchingUnifiedTax(false);
    }
  };
  
  // Debounced unified tax suggestion fetch
  const lineItemDescriptions = lineItems.map(i => `${i.description || ''}|${i.category || ''}`).join(',');
  const lineItemAmounts = lineItems.map(i => i.amount || '0').join(',');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.vendorName || lineItems.some(item => item.description || item.category)) {
        // Reset previously applied WHT when inputs change, so new suggestion is visible
        setSelectedWhtRate(null);
        setShowWhtOptions(false);
        fetchUnifiedTaxSuggestion();
      }
    }, 1200);
    
    return () => clearTimeout(timer);
  }, [formData.vendorName, formData.vendorEntityType, lineItemDescriptions, lineItemAmounts]);
  
  // Apply AI-suggested WHT rate
  const applyAiWhtRate = () => {
    if (aiWhtSuggestion) {
      setSelectedWhtRate(aiWhtSuggestion.suggested_wht_rate);
      // REMOVED: Auto-selection of entity type - user must explicitly choose
      // WHT should only apply when user intentionally selects entity type
      setShowWhtOptions(false);
      toast.success(`Applied ${aiWhtSuggestion.suggested_wht_rate}% WHT rate`);
    }
  };
  
  // Calculate WHT amount based on selected rate
  const calculateWhtAmount = () => {
    // WHT is on the face value (net of VAT) — same base the backend and the invoice page use
    const total = calculateTotal() - calculateTotalTax();
    if (selectedWhtRate && selectedWhtRate > 0 && total > 0) {
      return round(total * (selectedWhtRate / 100), 2);
    }
    return 0;
  };
  
  // Calculate final payable amount after WHT
  const calculatePayableAmount = () => {
    const total = calculateTotal();
    const wht = calculateWhtAmount();
    return round(total - wht, 2);
  };
  
  // Helper for rounding
  const round = (num, decimals) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };
  
  // Recalculate WHT preview when line items change
  useEffect(() => {
    if (formData.vendorEntityType) {
      calculateWHTPreview(formData.vendorEntityType);
    }
  }, [lineItems]);

  // Bank registry (fetched from backend) for intelligent redirection
  const [bankRegistry, setBankRegistry] = useState([]);
  const [bankAccountType, setBankAccountType] = useState('retail'); // 'retail' or 'corporate'

  // Useful life options based on asset type
  const usefulLifeOptions = [
    { value: '12', label: '1 year (Prepaid, Software Subscriptions)' },
    { value: '24', label: '2 years (Short-term Prepayments)' },
    { value: '36', label: '3 years (Computers, Electronics, Patents)' },
    { value: '60', label: '5 years (Furniture, Vehicles, Machinery)' },
    { value: '84', label: '7 years (Office Equipment)' },
    { value: '120', label: '10 years (Heavy Machinery, Trademarks)' },
    { value: '180', label: '15 years (Improvements)' },
    { value: '240', label: '20+ years (Buildings, Property)' },
  ];

  const getLocalCurrencySymbol = (currency = null) => {
    // Use explicitly passed currency, then billCurrency, then business currency
    const curr = currency || billCurrency || business?.currency || 'NGN';
    return getCurrencySymbol(curr);
  };

  // Get the business's BASE currency
  const getBaseCurrency = () => {
    return business?.currency || 'NGN';
  };
  
  // Get the current working currency (bill currency if set, otherwise base currency)
  const getCurrency = () => {
    return billCurrency || getBaseCurrency();
  };

  // Find matching bank entry from registry (fuzzy match by name/aliases)
  const findBankInRegistry = (bankName) => {
    if (!bankName || bankRegistry.length === 0) return null;
    const nameLower = bankName.toLowerCase().trim();
    return bankRegistry.find(b => {
      if (b.name.toLowerCase() === nameLower) return true;
      if (nameLower.includes(b.name.toLowerCase()) || b.name.toLowerCase().includes(nameLower)) return true;
      return (b.aliases || []).some(a => {
        const aliasLower = a.toLowerCase();
        return aliasLower === nameLower || nameLower.includes(aliasLower) || aliasLower.includes(nameLower);
      });
    });
  };

  // Generate the correct bank redirection URL based on OS and account type
  const getBankRedirectUrl = (bankName) => {
    const bankConfig = findBankInRegistry(bankName);
    if (!bankConfig) return null;

    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isAndroid) {
      // Android: try intent deep link first, falls back to Play Store
      if (bankConfig.android_package) {
        return {
          type: 'android_app',
          primary: `intent://#Intent;package=${bankConfig.android_package};end`,
          fallback: bankConfig.android_playstore_url || `https://play.google.com/store/apps/details?id=${bankConfig.android_package}`,
          label: 'Open App'
        };
      }
    } else if (isIOS) {
      // iOS: try custom scheme, fall back to App Store
      if (bankConfig.ios_scheme || bankConfig.ios_appstore_url) {
        return {
          type: 'ios_app',
          primary: bankConfig.ios_scheme || bankConfig.ios_appstore_url,
          fallback: bankConfig.ios_appstore_url,
          label: 'Open App'
        };
      }
    }

    // Desktop / fallback: use web banking URL based on account type
    const webUrl = bankAccountType === 'corporate'
      ? (bankConfig.web_corporate || bankConfig.web_retail)
      : (bankConfig.web_retail || bankConfig.web_corporate);
    
    if (webUrl) {
      return { type: 'web', primary: webUrl, fallback: null, label: 'Open Internet Banking' };
    }
    return null;
  };

  // Fetch bank accounts on mount
  useEffect(() => {
    fetchBankAccounts();
    fetchTaxConfig();
    fetchChartOfAccounts(); // CRITICAL: Load COA for GL selection
    fetchBankRegistry(); // Load bank registry for smart payment redirection
    
    // Check for AI-prefilled data from sessionStorage
    const prefillData = sessionStorage.getItem('ai_prefill_bill');
    if (prefillData) {
      try {
        const data = JSON.parse(prefillData);
        if (data.prefilled_from_ai) {
          // Populate form with AI / scan data
          setFormData(prev => ({
            ...prev,
            vendorName: data.vendor_name || '',
            description: data.description || '',
            category: data.category || ''
          }));

          // iter1307 - multi-currency scan: carry the detected currency +
          // FX rate into the form so the FXRatePicker opens pre-configured.
          if (data.currency && data.currency !== (business?.currency || 'NGN')) {
            setBillCurrency(data.currency);
            if (data.fx_rate) {
              setFxRate(Number(data.fx_rate) || 1);
              setFxRateSource(data.fx_rate_source || 'live');
            }
          }
          
          // Set line items - use scan items if available, otherwise single amount
          if (data.items && data.items.length > 0) {
            setLineItems(data.items.map((item, idx) => ({
              id: idx + 1,
              description: item.name || item.description || '',
              category: data.category || '',
              glCode: '',
              amount: (item.total || item.unit_price || 0).toString(),
              isAsset: false,
              assetType: '',
              taxCode: data.tax_amount > 0 ? 'VAT' : 'NONE',
              taxRate: data.tax_amount > 0 && data.amount > 0 
                ? Math.round((data.tax_amount / data.amount) * 1000) / 10 
                : 0
            })));
          } else if (data.amount) {
            setLineItems([{
              id: 1,
              description: data.description || '',
              category: data.category || '',
              glCode: '',
              amount: data.amount?.toString() || '',
              isAsset: false,
              assetType: '',
              taxCode: data.tax_amount > 0 ? 'VAT' : 'NONE',
              taxRate: data.tax_amount > 0 && data.amount > 0
                ? Math.round((data.tax_amount / data.amount) * 1000) / 10
                : 0
            }]);
          }
          
          // Set bill date if provided
          if (data.bill_date) {
            try { setBillDate(new Date(data.bill_date)); } catch (e) {}
          }
          
          // Set due date if provided
          if (data.due_date) {
            try { setDueDate(new Date(data.due_date)); } catch (e) {}
          }
          
          // Show toast to inform user
          toast.success('Bill form pre-filled from AI! Review and make any changes before saving.', {
            duration: 5000
          });
          
          // Clear the sessionStorage
          sessionStorage.removeItem('ai_prefill_bill');
        }
      } catch (error) {
        console.error('Error parsing AI prefill data:', error);
        sessionStorage.removeItem('ai_prefill_bill');
      }
    }
    
    // Check for bank feed prefill data - prop wins, then location.state.
    const bankFeedPrefill = prefillOverride || location.state?.bankFeedPrefill;
    const isSplitTransaction = location.state?.splitTransaction || bankFeedPrefill?.split_transaction;
    
    if (bankFeedPrefill) {
      setFormData(prev => ({
        ...prev,
        vendorName: bankFeedPrefill.counterparty || '',
        description: bankFeedPrefill.description || '',
        reference: bankFeedPrefill.reference || ''
      }));
      
      // For split transactions, start with empty line items so user can add multiple lines
      // For regular bank feed, prefill with single line
      if (isSplitTransaction) {
        // Start with one empty line for split - user will add more
        setLineItems([{
          id: 1,
          description: '',
          category: '',
          glCode: '',
          amount: '',
          isAsset: false,
          assetType: '',
          taxCode: 'NONE',
          taxRate: 0
        }]);
        
        // Store the expected total for split validation
        setSplitExpectedTotal(Math.abs(bankFeedPrefill.amount || 0));
        setIsSplitMode(true);
      } else {
        // "Not Quite Right" mode - single line with full amount LOCKED
        // User can change GL code and description, but NOT the amount
        const prefillAmount = Math.abs(bankFeedPrefill.amount || 0);
        setLineItems([{
          id: 1,
          description: bankFeedPrefill.description || 'Bank Feed Transaction',
          category: '',
          glCode: bankFeedPrefill.gl_debit || '',
          amount: prefillAmount.toString(),
          isAsset: false,
          assetType: '',
          taxCode: 'NONE',
          taxRate: 0
        }]);
        
        // Enable "Not Quite Right" mode - locks amount and prevents adding lines
        setIsNotQuiteRightMode(true);
        setLockedAmount(prefillAmount);
      }
      
      if (bankFeedPrefill.date) {
        setBillDate(new Date(bankFeedPrefill.date));
        setDueDate(new Date(bankFeedPrefill.date));
      }
      
      if (bankFeedPrefill.bank_account_id) {
        setSelectedBankId(bankFeedPrefill.bank_account_id);
      }
      
      // Track bank feed transaction ID for completion
      if (bankFeedPrefill.bank_feed_transaction_id) {
        setBankFeedTransactionId(bankFeedPrefill.bank_feed_transaction_id);
        setOriginalBankFeedStatus(bankFeedPrefill.original_status || null);
        setIsFromBankFeed(true);
      }
      // P2 · the locked amount is in the BANK account's currency → document currency follows it
      if (bankFeedPrefill.currency && bankFeedPrefill.currency.toUpperCase() !== getBaseCurrency()) {
        setBillCurrency(bankFeedPrefill.currency.toUpperCase());
      }
      
      // CRITICAL: Force VAT to inclusive mode for bank feed transactions
      // The bank amount is sacred - VAT must be computed WITHIN it, not added on top
      setVatMode('inclusive');
      
      // Hide AI mode when prefilled from bank feed
      setShowAIMode(false);
      
      // Different toast messages for split vs reclassify
      if (isSplitTransaction) {
        toast.info('Split Transaction Mode: Add multiple line items. Total must equal the original transaction amount.', {
          duration: 8000
        });
      } else {
        toast.info('Form pre-filled from bank transaction. Amount, date, and bank account are locked to match the bank record.', {
          duration: 5000
        });
      }
    }
  }, [location.state]);

  // Debounced tax suggestion fetch when line items change
  // Tax suggestions are now handled by the unified fetchUnifiedTaxSuggestion above
  
  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API}/bank-accounts`);
      const usable = res.data;
      setBankAccounts(usable);
      setBankAccountsLoaded(true);
      
      // iter1224 - never default to a virtual Stripe Clearing account for
      // bill payments (pass-through GL, not a payable rail).
      const payable = usable.filter(b => b.source !== 'stripe');
      if (payable.length === 0) {
        setShowForceBankModal(true);
      } else {
        const defaultBank = payable.find(b => b.is_default) || payable[0];
        if (defaultBank) setSelectedBankId(defaultBank.id);
      }
    } catch (err) {
      console.log('Could not fetch bank accounts');
      setBankAccountsLoaded(true);
    }
  };

  const fetchTaxConfig = async () => {
    try {
      // Fetch from centralized tax management API
      const response = await axios.get(`${API}/tax-management/rules`);
      const rules = response.data?.rules || [];
      
      // For bills, the per-line tax dropdown shows only VAT (input) options
      // WHT is handled separately in the WHT suggestion section
      const billTaxes = rules.filter(r => 
        r.tax_type === 'input' && r.is_active
      );
      
      // Convert to the format expected by the tax dropdown
      const taxes = billTaxes.map(r => ({
        code: r.code,
        name: r.name,
        rate: r.rate,
        gl_account_code: r.gl_account_code,
        is_default: r.is_default,
        tax_type: r.tax_type
      }));
      
      setTaxConfig({ taxes });
      
      // If no taxes, try to initialize them
      if (taxes.length === 0) {
        try {
          await axios.post(`${API}/tax-management/initialize`);
          // Re-fetch after initialization
          const refreshed = await axios.get(`${API}/tax-management/rules`);
          const refreshedRules = refreshed.data?.rules || [];
          const refreshedBillTaxes = refreshedRules.filter(r => 
            (r.tax_type === 'input' || r.tax_type === 'withholding') && r.is_active
          );
          setTaxConfig({ 
            taxes: refreshedBillTaxes.map(r => ({
              code: r.code,
              name: r.name,
              rate: r.rate,
              gl_account_code: r.gl_account_code,
              is_default: r.is_default,
              tax_type: r.tax_type
            }))
          });
        } catch (initError) {
          console.log('Tax initialization skipped:', initError);
        }
      }
    } catch (error) {
      console.log('Loading tax config...', error);
      // Fallback to old API
      try {
        const fallback = await axios.get(`${API}/products-services/tax-config`);
        setTaxConfig(fallback.data?.config || null);
      } catch (fallbackError) {
        console.log('Fallback tax config also failed');
      }
    }
  };

  // Fetch AI tax suggestions for line items
  const fetchTaxSuggestions = async (items) => {
    if (!items || items.length === 0) return;
    
    // Only fetch suggestions for items with descriptions/categories
    const itemsWithData = items.filter(item => 
      (item.description && item.description.length >= 2) || 
      (item.category && item.category.length >= 2)
    );
    if (itemsWithData.length === 0) return;
    
    setFetchingSuggestions(true);
    try {
      const requestItems = itemsWithData.map(item => ({
        description: item.description || item.category,
        amount: parseFloat(item.amount) || 0,
        category: item.category || 'expenses',
        product_id: item.matchedProduct?.id || item.productId || null,
      }));
      
      const response = await axios.post(`${API}/tax-management/suggest`, {
        transaction_type: 'bill',
        items: requestItems,
        contact_id: selectedVendorId || null
      });
      
      if (response.data?.suggestions) {
        const newSuggestions = {};
        itemsWithData.forEach((item, idx) => {
          if (response.data.suggestions[idx]) {
            const suggested = response.data.suggestions[idx];
            const defaultTax = suggested.find(s => s.is_default);
            if (defaultTax) {
              newSuggestions[item.id] = defaultTax.code;
            } else if (suggested.length > 0) {
              newSuggestions[item.id] = suggested[0].code;
            }
          }
        });
        setSuggestedTaxes(newSuggestions);
      }
    } catch (error) {
      console.log('Tax suggestions not available:', error);
    } finally {
      setFetchingSuggestions(false);
    }
  };

  // Apply AI suggested tax to a line item
  const applySuggestedTax = (itemId) => {
    const suggestedCode = suggestedTaxes[itemId];
    if (!suggestedCode || !taxConfig?.taxes) return;
    
    const suggestedTax = taxConfig.taxes.find(t => t.code === suggestedCode);
    if (suggestedTax) {
      setLineItems(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, taxCode: suggestedCode, taxRate: suggestedTax.rate }
          : item
      ));
      toast.success(`Applied ${suggestedTax.name} (${suggestedTax.rate}%)`);
    }
  };

  // Apply AI suggested taxes (BOTH VAT + WHT) to all applicable line items
  const applyAllSuggestedTaxes = () => {
    // Validate required fields
    const missingFields = [];
    if (!formData.vendorName?.trim()) missingFields.push('Vendor Name');
    if (!formData.vendorEntityType && formData.vendorName) setFormData(prev => ({ ...prev, vendorEntityType: inferEntityTypeFromName(formData.vendorName) }));
    const hasDescription = lineItems.some(item => item.description?.trim());
    if (!hasDescription) missingFields.push('Line Item Description');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')} before applying tax suggestions`);
      return;
    }
    
    // If no suggestions loaded yet, trigger fetch
    if (!aiTaxSuggestion && Object.keys(suggestedTaxes).length === 0) {
      toast.info('Fetching AI tax suggestions...');
      fetchUnifiedTaxSuggestion();
      return;
    }
    
    let vatApplied = 0;
    let whtApplied = false;
    
    // Apply per-line VAT suggestions
    if (taxConfig?.taxes && Object.keys(suggestedTaxes).length > 0) {
      setLineItems(prev => prev.map(item => {
        const suggestedCode = suggestedTaxes[item.id];
        if (suggestedCode && (!item.taxCode || item.taxCode === 'NONE')) {
          const suggestedTax = taxConfig.taxes.find(t => t.code === suggestedCode && t.tax_type !== 'withholding');
          if (suggestedTax) {
            vatApplied++;
            return { ...item, taxCode: suggestedCode, taxRate: suggestedTax.rate };
          }
        }
        return item;
      }));
    }
    
    // Apply WHT suggestion (bill-level)
    if (aiWhtSuggestion?.is_applicable && aiWhtSuggestion.suggested_wht_rate > 0 && !selectedWhtRate) {
      setSelectedWhtRate(aiWhtSuggestion.suggested_wht_rate);
      if (aiWhtSuggestion.suggested_entity_type && !formData.vendorEntityType) {
        setFormData(prev => ({ ...prev, vendorEntityType: aiWhtSuggestion.suggested_entity_type }));
      }
      setShowWhtOptions(false);
      whtApplied = true;
    }
    
    // Show result
    const parts = [];
    if (vatApplied > 0) parts.push(`VAT on ${vatApplied} item${vatApplied > 1 ? 's' : ''}`);
    if (whtApplied) parts.push(`WHT ${aiWhtSuggestion.suggested_wht_rate}%`);
    if (parts.length > 0) {
      toast.success(`Applied: ${parts.join(' + ')}`);
    } else if (aiWhtSuggestion && !aiWhtSuggestion.is_applicable) {
      toast.info(`AI determined: No WHT applicable. ${aiWhtSuggestion.reasoning || ''}`);
    } else {
      toast.info('No new tax suggestions to apply');
    }
  };

  // Callback when bank is added from modal
  const handleBankAdded = () => {
    fetchBankAccounts();
    setShowForceBankModal(false);  // Close the mandatory modal
    toast.success('Bank account added! You can now select it for payments.');
  };

  // Iter 815 - When user lands here from the Fixed Asset / Prepayment schedule
  // page, the asset terms (isAsset, assetType, useful_life, GL code) are
  // already locked-in by the user's prior selection. The AI category-suggest
  // engine must NOT overwrite them when the user types into the search box.
  // We still let the suggestion engine compute the matched GL / suggested
  // description, but we don't touch the asset-classification state.
  const presetFromSchedule = isAssetFromUrl || isPrepaidFromUrl;

  // Smart AI category search
  const searchCategory = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCategorySuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const res = await axios.post(`${API}/bills/suggest-category`, {
        query: query,
        country: business?.country,
        industry: business?.industry
      });
      
      const suggestions = res.data.suggestions || [];
      setCategorySuggestions(suggestions);
      
      // ONLY use the TOP suggestion (highest confidence) to determine asset status
      // This fixes the bug where "Utilities" was incorrectly flagged as asset
      // because a lower-ranked asset suggestion appeared in the list
      if (suggestions.length > 0) {
        const topSuggestion = suggestions[0];
        
        // Iter 815 - Respect the preset asset terms from the schedule page.
        // Only adjust isAsset / assetType / matchedGLCode if the user did NOT
        // arrive with a pre-locked asset/prepayment definition.
        if (!presetFromSchedule) {
          // Set asset status based on the TOP suggestion only
          const isTopSuggestionAsset = topSuggestion.is_asset === true;
          setIsAsset(isTopSuggestionAsset);

          // Check if this is a prepaid expense (requires payment, can't just approve)
          const requiresPayment = topSuggestion.requires_payment === true ||
                                  topSuggestion.asset_type === 'amortizable_prepaid';
          setIsPrepaidExpense(requiresPayment);

          if (isTopSuggestionAsset) {
            setAssetType(topSuggestion.asset_type || 'depreciable');
          } else {
            // Reset asset type if top suggestion is not an asset
            setAssetType('');
          }

          if (topSuggestion.gl_code) {
            setMatchedGLCode(topSuggestion.gl_code);
          }
        }
        // Set suggested description regardless - useful for both flows
        if (topSuggestion.suggested_description) {
          setSuggestedDescription(topSuggestion.suggested_description);
        }
      } else if (!presetFromSchedule) {
        // No suggestions AND no preset - reset asset state
        setIsAsset(false);
        setAssetType('');
        setIsPrepaidExpense(false);
      }
    } catch (err) {
      // Fallback: fetch from Chart of Accounts and do simple matching
      try {
        const coaResponse = await axios.get(`${API}/accounting/chart-of-accounts`);
        const allAccounts = coaResponse.data?.accounts || coaResponse.data || [];
        const expenseAssetAccounts = allAccounts.filter(acc => 
          ['expense', 'asset'].includes(acc.type)
        );
        const queryLower = query.toLowerCase();
        const matched = expenseAssetAccounts
          .filter(acc => acc.name.toLowerCase().includes(queryLower) || acc.code.includes(queryLower))
          .slice(0, 5)
          .map(acc => ({
            name: acc.name,
            gl_code: acc.code,
            type: acc.type,
            is_asset: acc.type === 'asset',
            confidence: acc.name.toLowerCase().startsWith(queryLower) ? 0.9 : 0.7
          }));
        setCategorySuggestions(matched);
      } catch (coaError) {
        console.log('Could not fetch from COA as fallback', coaError);
        setCategorySuggestions([]);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [business?.country, business?.industry]);

  // Debounce category search
  useEffect(() => {
    if (categorySearch) {
      const timer = setTimeout(() => {
        searchCategory(categorySearch);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [categorySearch, searchCategory]);

  // Select a category suggestion
  const selectCategory = (suggestion) => {
    // Set suggested description if vendor is known
    let description = suggestion.suggested_description || '';
    if (formData.vendorName && description) {
      description = description.replace('{vendor}', formData.vendorName);
    }
    
    // Iter 815 - When preset from schedule, preserve the existing glCode
    // (the schedule already chose it). Only auto-fill description.
    setFormData(prev => ({
      ...prev,
      category: suggestion.name,
      glCode: presetFromSchedule ? (prev.glCode || suggestion.gl_code) : suggestion.gl_code,
      description: prev.description || description  // Only set if not already filled
    }));
    setCategorySearch(suggestion.name);
    setCategorySuggestions([]);
    if (!presetFromSchedule) {
      setMatchedGLCode(suggestion.gl_code);
    }
    setSuggestedDescription(description);

    // Iter 815 - When preset from schedule, do NOT touch asset state on
    // suggestion click - the schedule's terms are authoritative. Tax
    // suggestions (vat/wht) still flow through normally below.
    if (presetFromSchedule) {
      return;
    }

    // If it's an asset, show the asset dialog
    if (suggestion.is_asset) {
      setIsAsset(true);
      setAssetType(suggestion.asset_type || 'depreciable');
      // Check if this is a prepaid expense (requires payment)
      setIsPrepaidExpense(suggestion.requires_payment === true || 
                          suggestion.asset_type === 'amortizable_prepaid');
      if (suggestion.useful_life) {
        setAssetUsefulLife(suggestion.useful_life.toString());
      } else {
        setAssetUsefulLife(''); // Reset if no default
      }
      setShowAssetDialog(true);
    } else {
      // Reset all asset-related states when selecting an expense
      setIsAsset(false);
      setAssetType('');
      setAssetUsefulLife('');
      setIsPrepaidExpense(false);
      setShowAssetDialog(false);
    }
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    
    // Check if accounting start date is set - block save if not
    if (business && !business.accounting_start_date) {
      setShowStartDateModal(true);
      return;
    }
    
    if (!formData.vendorName.trim()) {
      toast.error('Please enter vendor/payee name');
      return;
    }

    // CRITICAL: Vendor type is required so the WHT report can classify every
    // transaction (corporate vs individual). No more "unclassified" rows.
    // Drafts skip this so users can save in-progress work, but any non-draft
    // save MUST set vendor_entity_type.
    if (action !== 'draft' && !formData.vendorEntityType && formData.vendorName) {
      formData.vendorEntityType = inferEntityTypeFromName(formData.vendorName);
    }
    if (action !== 'draft' && !formData.vendorEntityType) {
      toast.error('Please select Vendor Type (Corporate or Individual): required for WHT classification', {
        description: 'This ensures the WHT report has no unclassified transactions.'
      });
      // Try to scroll the field into view for a nicer UX
      try {
        const el = document.querySelector('[data-testid="vendor-entity-type-select"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) { /* noop */ }
      return;
    }

    // Validate line items - every line carrying an amount is posted; a missing
    // description falls back to the GL name / bill description so a split never
    // silently drops a line (the total is locked to the bank row).
    const validItems = lineItems
      .filter(item => parseFloat(item.amount) > 0)
      .map(item => ({ ...item, description: (item.description || '').trim() || item.category || formData.description || 'Line item' }));
    if (validItems.length === 0) {
      toast.error('Please add at least one item with description and amount');
      return;
    }
    
    // CRITICAL: Validate that all line items have a GL code selected from COA
    const itemsWithoutGL = validItems.filter(item => !item.glCode || item.glCode.trim() === '');
    if (itemsWithoutGL.length > 0) {
      toast.error('Please select a category from the Chart of Accounts for all line items');
      return;
    }
    
    const totalAmount = calculateTotal();
    if (totalAmount <= 0) {
      toast.error('Total amount must be greater than zero');
      return;
    }

    // CRITICAL: Split transaction validation - line items must sum to expected total
    if (isSplitMode && Math.abs(totalAmount - splitExpectedTotal) >= 0.01) {
      const remaining = Math.abs(splitExpectedTotal - totalAmount);
      toast.error(`Split transaction incomplete: Line items must equal ${formatCurrency(splitExpectedTotal, billCurrency || getBaseCurrency())}. Remaining: ${getLocalCurrencySymbol()}${formatDisplayNumber(remaining)}`);
      return;
    }

    // For payment actions, bank selection is optional
    // If no bank selected, payment will be recorded to Cash account (1010)
    if (action === 'pay' && !selectedBankId) {
      // Show info that cash account will be used
      toast.info('Payment will be recorded to Cash account', {
        description: 'No bank account selected - using Cash (GL: 1010)'
      });
    }

    // Check for any asset items without useful life
    const assetItems = lineItems.filter(item => item.isAsset && !assetUsefulLife);
    if (assetItems.length > 0 && action !== 'save' && action !== 'draft') {
      setShowAssetDialog(true);
      toast.error('Please specify the useful life for asset items');
      return;
    }

    setLoading(true);
    setSaveAction(action);

    try {
      // Duplicate check before save (non-blocking on failure)
      if (action !== 'draft') {
        const dateStr = formData.billDate instanceof Date ? formData.billDate.toISOString().split('T')[0] : formData.billDate;
        const shouldProceed = await checkForDuplicates({
          amount: totalAmount, date: dateStr,
          description: formData.vendorName, document_type: 'bill',
          // No exclude_id on the Create page - we're not editing an existing
          // bill. (Referencing an `isEditing` variable that doesn't exist here
          // threw a ReferenceError and silently broke Save - bug fix.)
          exclude_id: undefined,
        });
        if (!shouldProceed) { setLoading(false); return; }
      }

      // Use the first item's category as the main category (or generate combined description)
      const primaryItem = validItems[0];
      const combinedDescription = validItems.map(i => `${i.description}: ${getLocalCurrencySymbol()}${formatDisplayNumber(i.amount)}`).join('; ');
      
      // Period lock check for bank feed transactions (skip for drafts - no journal posting)
      if (isFromBankFeed && action !== 'draft' && !periodLockOverrideRef.current) {
        const dateStr = format(billDate, 'yyyy-MM-dd');
        pendingSubmitActionRef.current = action;
        const locked = await checkPeriodLock(
          dateStr,
          async () => {
            periodLockOverrideRef.current = true;
            periodLockOverrideDateRef.current = null;
            await handleSubmit({ preventDefault: () => {} }, pendingSubmitActionRef.current);
            periodLockOverrideRef.current = false;
          },
          async () => {
            periodLockOverrideRef.current = true;
            periodLockOverrideDateRef.current = new Date().toISOString().split('T')[0];
            await handleSubmit({ preventDefault: () => {} }, pendingSubmitActionRef.current);
            periodLockOverrideRef.current = false;
            periodLockOverrideDateRef.current = null;
          }
        );
        if (locked) { setLoading(false); return; }
      }
      
      // BANK FEED OVERRIDE: Ensure the net payable matches the bank amount exactly
      // The bank amount is sacred - VAT and WHT must be computed WITHIN it
      const billSecondaryTax = Math.round(calculateSecondaryTax() * 100) / 100;
      let billFaceValue = calculateTotal() - calculateTotalTax() - billSecondaryTax;
      let billTaxTotal = calculateTotalTax();
      let billTotal = calculateTotal();
      let billWhtAmount = 0;
      let billNetPayable = billTotal;
      
      if (isFromBankFeed && lockedAmount > 0) {
        const bankAmount = lockedAmount;
        
        if (selectedWhtRate && selectedWhtRate > 0) {
          // When WHT is applied on a bill, back-calculate so net_payable = bank_amount
          // net_payable = face_value + VAT - WHT = bank_amount
          // face_value = bank_amount / (1 + vat_rate - wht_rate)
          const effectiveVatRate = (lineItems[0]?.taxRate || 0) / 100;
          const effectiveWhtRate = selectedWhtRate / 100;
          const divisor = 1 + effectiveVatRate - effectiveWhtRate;
          
          if (divisor > 0) {
            billFaceValue = Math.round(bankAmount / divisor * 100) / 100;
            billTaxTotal = Math.round(billFaceValue * effectiveVatRate * 100) / 100;
            billTotal = Math.round((billFaceValue + billTaxTotal) * 100) / 100;
            billWhtAmount = Math.round(billTotal - bankAmount);
            billNetPayable = bankAmount;
          }
        } else {
          // No WHT - ensure total = bank_amount exactly (fix rounding)
          billTotal = bankAmount;
          billFaceValue = lineItems.reduce((sum, item) => {
            const qty = parseFloat(item.quantity) || 1;
            const amt = parseFloat(item.amount) || 0;
            return sum + (qty * amt);
          }, 0);
          billTaxTotal = Math.round((billTotal - billFaceValue) * 100) / 100;
          billNetPayable = billTotal;
        }
      }
      
      const effectiveBillDate = periodLockOverrideDateRef.current || format(billDate, 'yyyy-MM-dd');
      // iter971k-X · Edit mode: PUT /bills/{id}. Else POST.
      const billApiUrl = isEditMode
        ? (periodLockOverrideRef.current
            ? `${API}/bills/${editBillId}?allow_locked_period=true`
            : `${API}/bills/${editBillId}`)
        : (periodLockOverrideRef.current
            ? `${API}/bills?allow_locked_period=true`
            : `${API}/bills`);
      const billHttpMethod = isEditMode ? 'put' : 'post';

      const billData = {
        vendor_name: formData.vendorName,
        // amount = face value (net of VAT); total_amount = gross. Inclusive mode backs VAT out of the entered gross.
        amount: Math.round(billFaceValue * 100) / 100,
        total_amount: Math.round(billTotal * 100) / 100,
        project_id: billProjectId || (isEditMode ? '' : null),
        bill_number: formData.billNumber?.trim() || null,  // iter971k-W · null → server auto-generate
        // BANK FEED: Force vat_mode to inclusive when from bank feed
        vat_mode: isFromBankFeed ? 'inclusive' : vatMode,
        currency: billCurrency || getBaseCurrency(),
        fx_rate: billCurrency && billCurrency !== getBaseCurrency() ? fxRate : null,
        fx_rate_source: billCurrency && billCurrency !== getBaseCurrency() ? fxRateSource : null,
        base_currency: getBaseCurrency(),
        base_currency_amount: billCurrency && billCurrency !== getBaseCurrency() ? totalAmount * fxRate : null,
        description: validItems.length > 1 ? combinedDescription : (primaryItem.description || primaryItem.category),
        category: primaryItem.category,  // MUST be from COA selection - validation ensures this
        gl_code: primaryItem.glCode,  // MUST be from COA selection - validation ensures this
        bill_date: effectiveBillDate,  // Bill date (may be overridden for period lock)
        due_date: format(dueDate, 'yyyy-MM-dd'),
        supporting_documents: supportingDocs.map(d => d.id),
        pay_from_bank_id: selectedBankId || null,
        // For "Approve & Pay", we approve (recognition only) - payment posts
        // later via PATCH /bills/{id}/pay when user confirms Payment Complete
        mark_paid: action === 'mark_paid',
        // iter971k-Z · Turn 5c - user-picked payment date for settlement journal
        mark_paid_date: action === 'mark_paid' && confirmedPaymentDate
          ? format(confirmedPaymentDate, 'yyyy-MM-dd')
          : null,
        initiate_payment: action === 'pay',
        // Draft flag - if true, no journal entry is posted
        is_draft: action === 'draft',
        // Asset specific fields - use first asset item's settings
        // If user cancelled asset setup, don't treat as asset
        is_asset: assetSetupCancelled ? false : validItems.some(i => i.isAsset),
        asset_type: assetSetupCancelled ? null : (primaryItem.isAsset ? primaryItem.assetType : null),
        asset_useful_life_months: (isAsset && !assetSetupCancelled) ? parseInt(assetUsefulLife) : null,
        depreciation_method: (isAsset && !assetSetupCancelled) ? depreciationMethod : null,
        // Depreciation/Amortization start date (calculated based on 15th rule)
        depreciation_start_date: (isAsset && !assetSetupCancelled && assetUsefulLife) ? calculateDepreciationStartDate() : null,
        // Prepaid expense specific fields (when coming from Prepaid Expenses page)
        prepaid_type: isPrepaidFromUrl ? prepaidTypeFromUrl : null,
        prepaid_gl_code: isPrepaidFromUrl ? prepaidGlCodeFromUrl : null,
        expense_gl_code: isPrepaidFromUrl ? (prepaidExpenseGl || null) : null,
        spread_over_months: isPrepaidFromUrl ? parseInt(spreadMonthsFromUrl) : null,
        // WHT-specific fields (Smart WHT Deduction feature)
        // iter 969 (Bug W) - apply_wht MUST require an explicit user-selected
        // WHT rate. Do NOT auto-enable just because a vendor entity type was
        // picked (entity type is a required field for other reasons, not for
        // WHT consent).
        vendor_id: selectedVendorId || null,
        vendor_entity_type: formData.vendorEntityType || (aiWhtSuggestion?.suggested_entity_type) || null,
        apply_wht: !!(selectedWhtRate && selectedWhtRate > 0),
        wht_rate_override: selectedWhtRate || null, // User-selected WHT rate
        // BANK FEED: Use back-calculated values for accurate journal entries
        face_value: billFaceValue,
        tax_amount: billTaxTotal,
        // Input VAT persisted explicitly (backend reads input_vat_amount / input_vat_rate, not tax_amount)
        apply_input_vat: billTaxTotal > 0,
        input_vat_amount: billTaxTotal,
        input_vat_rate: billTaxTotal > 0 ? (lineItems.find((i) => (parseFloat(i.taxRate) || 0) > 0)?.taxRate || 0) : 0,
        wht_amount: billWhtAmount || (selectedWhtRate && selectedWhtRate > 0 ? calculateWhtAmount() : 0),
        net_payable: isFromBankFeed && lockedAmount > 0 ? lockedAmount : billNetPayable,
        // Store line items as JSON for future reference
        line_items: validItems.map(item => ({
          description: item.description,
          category: item.category,
          gl_code: item.glCode,
          amount: parseFloat(item.amount),
          project_id: item.projectId || billProjectId || null,
          is_asset: assetSetupCancelled ? false : item.isAsset,
          asset_type: assetSetupCancelled ? null : item.assetType,
          // NEW: Include special item terms if configured
          special_item_terms: item.specialTerms || specialItemTerms[item.id] || null
        })),
        // Bank feed transaction completion - when coming from "Not Quite Right" flow
        bank_feed_transaction_id: bankFeedTransactionId || null,
        // Phase C · reverse charge (non-resident supplier, imported service) decided by the tax engine
        secondary_tax_label: billSecondaryTax > 0 ? secondaryTaxInfo.label : null,
        secondary_tax_rate: billSecondaryTax > 0 ? secondaryTaxInfo.rate : 0,
        secondary_tax_amount: billSecondaryTax,
        secondary_tax_recoverable: billSecondaryTax > 0 ? !!secondaryTaxInfo.recoverable : false,
        reverse_charge: !!aiTaxSuggestion?.suggestions?.some((s) => s?.reverse_charge || s?.decision?.reverse_charge),
        reverse_charge_rate: aiTaxSuggestion?.suggestions?.find((s) => s?.reverse_charge || s?.decision?.reverse_charge)?.decision?.rate || 0,
        complete_bank_feed: isFromBankFeed,
        // Split transaction handling - for reversing completed transactions
        is_split_transaction: isSplitMode,
        original_bank_feed_status: originalBankFeedStatus || location.state?.bankFeedPrefill?.original_status || null
      };

      const response = await axios[billHttpMethod](billApiUrl, billData);
      const billId = response.data.id;
      if (response.data.payment_posting_failed) {
        toast.error(response.data.payment_posting_error || 'Bill saved, but the payment could not be recorded. Use Record Payment to retry.', { duration: 9000 });
      }

      // iter1086h - Phase C follow-up: link pre-uploaded receipts to
      // the real bill_id so the unified panel shows them on the Bill
      // detail view AND cross-links to any matched bank-feed txn.
      if (billId && supportingDocs && supportingDocs.length) {
        await Promise.all(supportingDocs.map(d => {
          const docId = d.id || d.document_id;
          if (!docId) return Promise.resolve();
          return axios.post(`${API}/documents/${docId}/links`, {
            entity_type: 'bill',
            entity_id: billId,
            linked_via: 'upload',
          }).catch(() => {});
        }));
      }
      
      // Create open tasks for special items without configured terms
      const unconfiguredSpecialItems = Object.keys(pendingSpecialItems).filter(
        lineId => !specialItemTerms[lineId]
      );
      if (unconfiguredSpecialItems.length > 0 && billId) {
        try {
          for (const lineId of unconfiguredSpecialItems) {
            const pending = pendingSpecialItems[lineId];
            const lineItem = lineItems.find(li => li.id === parseInt(lineId) || li.id === lineId);
            await axios.post(`${API}/tasks`, {
              title: `Set up ${pending.type === 'fixed_asset' ? 'depreciation' : 'amortization'} terms`,
              description: `Bill "${formData.vendorName}" has a ${pending.type === 'fixed_asset' ? 'fixed asset' : 'prepaid expense'} line item "${lineItem?.description || ''}" that needs ${pending.type === 'fixed_asset' ? 'depreciation' : 'amortization'} schedule configuration.`,
              task_type: 'special_item_setup',
              priority: 'high',
              related_document_type: 'bill',
              related_document_id: billId,
              status: 'open'
            });
          }
          toast.warning(`${unconfiguredSpecialItems.length} item(s) need term setup: added to your open tasks`, { duration: 5000 });
        } catch (taskErr) {
          console.log('Failed to create open task:', taskErr);
        }
      }
      
      // Show contact creation/linking feedback
      if (response.data.contact_id) {
        if (response.data.contact_auto_created) {
          toast.success(`New vendor contact "${formData.vendorName}" created and linked!`, {
            icon: '🏢',
            duration: 3000
          });
        } else {
          toast.info(`Linked to existing vendor "${formData.vendorName}"`, {
            icon: '🔗',
            duration: 2000
          });
        }
      }
      
      // Handle partial payment action - create bill then immediately record partial payment
      if (action === 'partial_payment' && billId && partialPaymentAmount) {
        try {
          await axios.post(`${API}/bills/${billId}/partial-payment`, {
            amount: parseFloat(partialPaymentAmount),
            reference: partialPaymentRef || `Partial payment on creation`,
            bank_account_id: selectedBankId || null,
            // iter971k-Z · Turn 5c - user-picked payment date
            payment_date: partialPaymentDate ? format(partialPaymentDate, 'yyyy-MM-dd') : null,
          });
          toast.success(`Bill saved with partial payment of ${getLocalCurrencySymbol()}${formatDisplayNumber(partialPaymentAmount)} recorded!`);
          navigate('/bills');
          return;
        } catch (partialErr) {
          toast.error('Bill saved but partial payment failed. You can record it from the bill page.');
          navigate('/bills');
          return;
        }
      }
      
      // Success handling based on action and whether from bank feed
      if (isFromBankFeed) {
        // When completing a bank feed transaction, always go back to bank feeds
        toast.success('Bill created and bank transaction completed!', {
          description: 'The transaction has been moved from Review Needed to In Books.'
        });
        navigate('/bank-feeds?tab=transactions&filter=pending');
        return;
      }
      
      if (action === 'draft') {
        toast.success('Bill saved as draft', {
          description: 'No journal entries posted. You can approve this bill later.'
        });
        navigate('/bills');
      } else if (action === 'pay') {
        // Save the bill ID for payment confirmation
        setSavedBillId(billId);
        
        // Show confirmation dialog with bank redirect options
        setShowPaymentConfirmDialog(true);
        setLoading(false);
        
        toast.success('Bill approved and awaiting payment', {
          description: 'Complete the payment via your bank, then tap "Payment Complete".'
        });
      } else if (action === 'mark_paid') {
        const scheduleType = assetType === 'amortizable_prepaid' ? 'Amortization' : 'Depreciation';
        const assetMessage = isAsset 
          ? ` ${scheduleType} of ${getLocalCurrencySymbol()}${formatDisplayNumber((calculateTotal() / parseInt(assetUsefulLife)).toFixed(2))}/month will be recorded.`
          : '';
        toast.success(`Bill saved and marked as paid!${assetMessage}`);
        navigate('/bills');
      } else if (action === 'open_unified_after_save') {
        // iter1421 · Bill saved (no payment posted yet); route the
        // user to the detail page where the unified RecordSettlementModal
        // opens on mount via ?openPayment=1.
        toast.success('Bill saved. Record the payment below.');
        navigate(`/bills/${billId}?openPayment=1`);
      } else {
        toast.success('Bill approved and saved!', {
          description: 'Journal entries have been posted to the ledger.'
        });
        navigate('/bills');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (error.message) {
        toast.error(`Failed to save bill: ${error.message}`);
      } else {
        toast.error('Failed to save bill. Please check your inputs and try again.');
      }
      setLoading(false);
    } finally {
      if (action !== 'pay') {
        setLoading(false);
      }
      setSaveAction('save');
    }
  };

  // Handle opening bank app for payment
  const handleOpenBankForPayment = () => {
    setPaymentInProgress(true);
    const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
    const bankName = selectedBank?.bank_name;
    const redirect = getBankRedirectUrl(bankName);

    if (redirect) {
      window.open(redirect.primary, '_blank');
      // For mobile apps, also open fallback after a delay in case app isn't installed
      if ((redirect.type === 'android_app' || redirect.type === 'ios_app') && redirect.fallback) {
        setTimeout(() => {
          // Only open fallback if we're still on this page (app didn't launch)
          window.open(redirect.fallback, '_blank');
        }, 2500);
      }
    } else {
      // Bank not found in registry - open the bank's website via a targeted search
      const fallbackUrl = `https://www.google.com/search?q=${encodeURIComponent((bankName || 'bank') + ' internet banking login')}`;
      window.open(fallbackUrl, '_blank');
    }

    toast.info(`Complete the payment of ${getLocalCurrencySymbol()}${formatDisplayNumber(calculateTotal())} in your banking app`, {
      duration: 10000
    });
  };

  // Handle payment completion confirmation - posts the settlement journal
  // and marks the bill as paid via the backend.
  const handlePaymentComplete = async () => {
    if (!savedBillId) {
      setShowPaymentConfirmDialog(false);
      navigate('/bills');
      return;
    }
    setCompletingPayment(true);
    try {
      await axios.patch(`${API}/bills/${savedBillId}/pay`);
      setShowPaymentConfirmDialog(false);
      toast.success('Payment recorded! Bill marked as paid.', {
        description: 'The payment journal has been posted to your ledger.'
      });
      navigate('/bills');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record the payment. Please try again.');
    } finally {
      setCompletingPayment(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('document_type', 'bill_support');

    try {
      const response = await axios.post(`${API}/documents/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSupportingDocs(prev => [...prev, response.data]);
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const removeDoc = (docId) => {
    setSupportingDocs(prev => prev.filter(d => d.id !== docId));
  };

  // Handle AI extracted data - pre-fill the form
  const handleAIExtract = (extracted) => {
    // Pre-fill vendor info
    if (extracted.vendor_name) {
      setFormData(prev => ({
        ...prev,
        vendorName: extracted.vendor_name
      }));
    }
    
    // Pre-fill line items
    if (extracted.items && extracted.items.length > 0) {
      const newLineItems = extracted.items.map((item, idx) => ({
        id: idx + 1,
        description: item.description || '',
        amount: item.amount?.toString() || '',
        category: item.category || '',
        glCode: item.gl_code || '',
        taxCode: '',
        taxRate: 0,
        isAsset: extracted.is_asset || false,
        assetType: extracted.asset_type || ''
      }));
      setFormData(prev => ({ ...prev, lineItems: newLineItems }));
      
      // Handle asset detection
      if (extracted.is_asset) {
        setIsAsset(true);
        setAssetType(extracted.asset_type || 'depreciable');
        if (extracted.asset_type === 'amortizable_prepaid') {
          setIsPrepaidExpense(true);
        }
        // Show asset dialog for configuration
        setShowAssetDialog(true);
      }
    }
    
    // Set due date if provided
    if (extracted.due_days) {
      setDueDate(addDays(new Date(), extracted.due_days));
    }
    
    // Set notes/description
    if (extracted.notes) {
      setFormData(prev => ({ ...prev, notes: extracted.notes }));
    }
    
    // Hide AI mode and show the form
    setShowAIMode(false);
    
    toast.success('Form pre-filled! Review and adjust before saving.', {
      duration: 4000
    });
  };

  const { goBack } = useNavigationContext();

  // iter959 - the previous inline `Chrome` component was redefined on every
  // render, causing React to unmount/remount the entire form (including every
  // <input>) on each keystroke. On mobile that closes the software keyboard
  // after every character. We now branch on `embedded` directly so the input
  // tree is preserved across renders.

  const pageBody = (
    <>
      {DuplicateDialog}
      <div className={embedded ? "p-4 sm:p-6" : "p-6 lg:p-8 max-w-2xl mx-auto"} data-testid="create-bill-container">
        {/* Header - hidden in embedded mode */}
        {!embedded && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => goBack(isAssetFromUrl ? '/fixed-assets' : isPrepaidFromUrl ? '/prepaid-expenses' : '/bills')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isEditMode ? 'Edit Bill' : (isAssetFromUrl ? 'Record Fixed Asset Purchase' : isPrepaidFromUrl ? 'Record Prepaid Expense' : 'Record Bill')}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode
                  ? `Update ${existingBill?.bill_number || 'this bill'}`
                  : isAssetFromUrl
                    ? 'Complete the bill to record this asset acquisition'
                    : isPrepaidFromUrl
                      ? 'Complete the bill to record this prepaid expense'
                      : 'Record expenses, assets, or any outgoing payment'}
              </p>
            </div>
          </div>
        </div>
        )}
          {bankFeedLocked && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-3" data-testid="bill-bank-feed-lock-banner">
              <Lock className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-teal-900">Linked to a bank transaction</p>
                <p className="text-teal-800 text-xs mt-0.5">This bill was created from or settled by a Bank Feed transaction. Amount, currency, line items, dates and bank account are locked to the bank data — you can still change the vendor name, description and notes. To change the money side, undo the row in Bank Feeds.</p>
                <BankTxActions documentId={editBillId} allowUndo onUndone={() => goBack('/bills')} className="mt-2" />
              </div>
            </div>
          )}

        {/* Fixed Asset Flow Banner */}
        {isAssetFromUrl && (
          <div className="mb-6 p-4 bg-gradient-to-r from-[#108981]/10 to-[#5FC6C5]/10 rounded-lg border border-[#5FC6C5]/30" data-testid="asset-flow-banner">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-[#108981] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#108981]">Fixed Asset Purchase</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="font-medium">{assetNameFromUrl}</span> • {assetCategoryLabelFromUrl} • {assetUsefulLifeFromUrl} months useful life
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  GL: {assetGlCodeFromUrl} • Salvage: {getLocalCurrencySymbol()} {formatDisplayNumber(salvageValueFromUrl)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Prepaid Expense Flow Banner */}
        {isPrepaidFromUrl && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/10 to-purple-300/10 rounded-lg border border-purple-300/30" data-testid="prepaid-flow-banner">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-purple-700">Prepaid Expense</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="font-medium">{prepaidDescriptionFromUrl}</span> • {prepaidTypeLabelFromUrl} • {spreadMonthsFromUrl} months amortization
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Prepaid GL: {prepaidGlCodeFromUrl}
                </p>
                <div className="mt-2 max-w-md" data-testid="prepaid-expense-gl-field">
                  <p className="text-xs font-medium text-purple-700 mb-1">
                    Expense account for amortisation {prepaidTypeFromUrl === 'other' && <span className="text-red-500">*</span>}
                  </p>
                  <GLAccountCombobox
                    value={prepaidExpenseGl}
                    onChange={setPrepaidExpenseGl}
                    accountTypes={['expense']}
                    placeholder={prepaidTypeFromUrl === 'other' ? 'Choose where the monthly expense goes…' : 'Search expense account…'}
                    className="w-full bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => handleSubmit(e, 'save')} className="space-y-6">
          {/* iter971k-Z · Turn 5 - Edit-mode payment / status banner. */}
          {isEditMode && existingBill && (
            <EditBillStatusBanner
              bill={existingBill}
              api={API}
              onChanged={async () => {
                try {
                  const { data: fresh } = await axios.get(`${API}/bills/${editBillId}`);
                  setExistingBill(fresh);
                } catch (_e) { /* silent retry on next action */ }
              }}
              getLocalCurrencySymbol={getLocalCurrencySymbol}
              formatDisplayNumber={formatDisplayNumber}
            />
          )}
          {/* Vendor Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Who are you paying?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* iter971k-W · editable bill number - uniqueness enforced server-side */}
              <div className="space-y-2">
                <Label htmlFor="billNumber">Bill Reference / Number</Label>
                <Input
                  id="billNumber"
                  placeholder="e.g. BILL-00001"
                  value={formData.billNumber}
                  onChange={(e) => setFormData({ ...formData, billNumber: e.target.value })}
                  className="h-12"
                  data-testid="input-bill-number"
                />
                <p className="text-xs text-muted-foreground">
                  Auto-generated. Edit to use your own (must be unique).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendorName">Vendor / Payee *</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      id="vendorName"
                      placeholder="e.g. Landlord, MTN, Facebook Ads, Dell Computers"
                      value={formData.vendorName}
                      onChange={(e) => {
                        setFormData({ ...formData, vendorName: e.target.value });
                        // Search existing vendors for suggestions
                        if (e.target.value.length >= 2) {
                          searchVendorSuggestions(e.target.value);
                        }
                      }}
                      className="h-12"
                      data-testid="input-vendor-name"
                    />
                    {/* Vendor Suggestions Dropdown */}
                    {vendorSuggestions.length > 0 && formData.vendorName && (
                      <div className="absolute left-0 right-0 top-full z-20 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {vendorSuggestions.map((vendor) => (
                          <button
                            key={vendor.id}
                            type="button"
                            onClick={() => selectVendor(vendor)}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted transition-colors text-left text-sm"
                          >
                            <div>
                              <span className="font-medium">{vendor.name}</span>
                              {vendor.email && (
                                <span className="block text-xs text-muted-foreground">{vendor.email}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {vendor.is_contact && (
                                <span className="text-xs px-2 py-0.5 rounded bg-[#283F8B]/10 text-[#283F8B]">
                                  Contact
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                vendor.entity_type === 'corporate' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {vendor.entity_type === 'corporate' ? 'Corporate' : 'Individual'}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <ContactTaxProfilePrompt contactId={selectedVendorId} contactName={formData.vendorName} contactType="vendor" entityType={formData.vendorEntityType}
                  onContactSaved={(c) => { if (c?.id) selectVendor(c); if (c?.entity_type) setFormData(prev => ({ ...prev, vendorEntityType: c.entity_type })); }} className="mt-2" />
                {selectedVendorId && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#283F8B] flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Linked to existing contact
                    </p>
                    {/* iter971k-Z · Turn 4 - inline Edit Contact */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setContactEditOpen(true)}
                      className="h-7 text-xs"
                      data-testid="btn-edit-linked-vendor"
                    >
                      Edit contact
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Sep 2026 · vendor type comes from the contact profile (inferred from the name, editable in the tax-profile prompt / contact) */}
              {formData.vendorEntityType && (
                <p className="text-xs text-muted-foreground" data-testid="vendor-entity-type-derived">
                  Vendor type for WHT: <span className="font-medium">{formData.vendorEntityType === 'individual' ? 'Individual' : 'Corporate'}</span>
                  <span className="text-slate-400"> · from the supplier profile</span>
                </p>
              )}
              

            </CardContent>
          </Card>

          {/* FX Rate Picker - Show when foreign currency might be used */}
          <div className={financeLockClass} inert={financeLocked || undefined}>
          <FXRatePicker
            baseCurrency={getBaseCurrency()}
            rateDate={billDate instanceof Date ? billDate.toISOString().split('T')[0] : billDate}
            selectedCurrency={billCurrency || getBaseCurrency()}
            onCurrencyChange={(currency) => {
              setBillCurrency(currency);
              if (currency === getBaseCurrency()) {
                setFxRate(1);
                setFxRateSource('live');
              } else {
                // Auto-suggest bank account matching the selected currency
                const matchingBank = bankAccounts.find(b => b.currency === currency);
                if (matchingBank) {
                  setSelectedBankId(matchingBank.id);
                }
              }
            }}
            fxRate={fxRate}
            onRateChange={(rate) => {
              setFxRate(rate);
            }}
            amount={calculateTotal()}
            showConversion={billCurrency && billCurrency !== getBaseCurrency()}
            disabled={loading || financeLocked}
            initialRateSource={fxRateSource || 'live'}
          />
          </div>
          
          {/* Currency mismatch warning for bills */}
          {billCurrency && billCurrency !== getBaseCurrency() && selectedBankId && (
            (() => {
              const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
              const bankCurrency = selectedBank?.currency;
              if (bankCurrency && bankCurrency !== billCurrency) {
                const matchingBanks = bankAccounts.filter(b => b.source !== 'stripe' && b.currency === billCurrency);
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Currency mismatch detected</p>
                      <p className="text-amber-700">
                        Bill is in {billCurrency} but selected payment account is in {bankCurrency}.
                        {matchingBanks.length > 0 ? (
                          <> Consider using a {billCurrency} account for easier reconciliation.</>
                        ) : (
                          <> You may want to create a {billCurrency} bank account for foreign currency payments.</>
                        )}
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          {/* Line Items - Compact Single-Line Layout */}
          <Card className={financeLockClass} inert={financeLocked || undefined} data-testid="bill-line-items-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">What are you paying for?</CardTitle>
                <div className="flex items-center gap-2">
                  {/* AI Tax Suggestions Button */}
                  {(Object.keys(suggestedTaxes).length > 0 || (aiWhtSuggestion?.is_applicable && !selectedWhtRate) || fetchingUnifiedTax) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={applyAllSuggestedTaxes}
                      className="gap-1 text-[#108981] border-[#108981]/30 hover:bg-[#108981]/10"
                      data-testid="apply-all-tax-suggestions-btn"
                    >
                      <Wand2 className="w-4 h-4" />
                      Apply AI Tax Suggestions
                    </Button>
                  )}
                  {(fetchingSuggestions || fetchingUnifiedTax) && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {fetchingUnifiedTax ? 'Analyzing tax implications...' : 'Getting suggestions...'}
                    </span>
                  )}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addLineItem}
                    disabled={isNotQuiteRightMode}
                    data-testid="add-line-item-btn"
                    title={isNotQuiteRightMode ? "Amount is locked in 'Not Quite Right' mode. Use Split Transaction to add multiple lines." : ""}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>
              {/* Column Headers */}
              <div className="hidden md:grid grid-cols-12 gap-2 mt-4 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Category / GL</div>
                <div className="col-span-2 text-center">VAT</div>
                <div className="col-span-3 text-right">Amount</div>
                <div className="col-span-1"></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              <AnimatePresence>
                {lineItems.map((item, index) => (
                  <LineItemRow 
                    key={item.id}
                    item={item}
                    index={index}
                    updateLineItem={updateLineItem}
                    removeLineItem={removeLineItem}
                    lineItemsCount={lineItems.length}
                    getCurrencySymbol={getLocalCurrencySymbol}
                    searchCategory={searchCategory}
                    loadingSuggestions={loadingSuggestions}
                    categorySuggestions={categorySuggestions}
                    setIsAsset={setIsAsset}
                    setAssetType={setAssetType}
                    setAssetUsefulLife={setAssetUsefulLife}
                    setShowAssetDialog={setShowAssetDialog}
                    setIsPrepaidExpense={setIsPrepaidExpense}
                    taxConfig={taxConfig}
                    getTaxOptions={getTaxOptions}
                    calculateLineTax={calculateLineTax}
                    formatDisplayNumber={formatDisplayNumber}
                    suggestedTaxes={suggestedTaxes}
                    applySuggestedTax={applySuggestedTax}
                    business={business}
                    coaAccounts={coaAccounts}
                    isAmountLocked={isNotQuiteRightMode}
                    onRequestCreateAccount={() => setShowCreateAccountModal(true)}
                    onSpecialItemDetected={(lineItemId, itemType, account) => {
                      // Store the pending special item
                      setPendingSpecialItems(prev => ({
                        ...prev,
                        [lineItemId]: { type: itemType, account }
                      }));
                      setCurrentSpecialItemLineId(lineItemId);
                      setSpecialItemType(itemType);
                      
                      // If the line item already has an amount, open modal immediately
                      // (e.g., user changed description after entering amount)
                      const lineItem = lineItems.find(li => li.id === lineItemId);
                      const hasAmount = lineItem && parseFloat(lineItem.amount) > 0;
                      if (hasAmount && !specialItemTerms[lineItemId]) {
                        setTimeout(() => setShowSpecialItemModal(true), 300);
                      }
                      // Otherwise, modal will auto-open on amount blur
                    }}
                    onAmountBlur={(lineItemId) => {
                      // When user finishes typing amount, open the pending special item modal
                      if (pendingSpecialItems[lineItemId] && !specialItemTerms[lineItemId]) {
                        const pending = pendingSpecialItems[lineItemId];
                        setCurrentSpecialItemLineId(lineItemId);
                        setSpecialItemType(pending.type);
                        setShowSpecialItemModal(true);
                      }
                    }}
                  />
                ))}
              </AnimatePresence>
              
              {/* Totals Section - VAT Mode Toggle & Breakdown */}
              <div className="pt-4 mt-2 border-t border-border space-y-2">
                {/* VAT Mode Toggle */}
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-dashed border-muted-foreground/20">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">VAT Mode:</span>
                    {isFromBankFeed ? (
                      <div className="flex items-center gap-1.5">
                        <span className="px-3 py-1 text-xs rounded-md bg-background text-foreground shadow-sm">Inclusive</span>
                        <span className="text-xs text-amber-600">(Locked: bank amount is VAT-inclusive)</span>
                      </div>
                    ) : (
                    <div className="flex rounded-lg bg-muted p-0.5">
                      <button
                        type="button"
                        onClick={() => setVatMode('inclusive')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          vatMode === 'inclusive' 
                            ? 'bg-background text-foreground shadow-sm' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="vat-mode-inclusive"
                        aria-pressed={vatMode === 'inclusive'}
                      >
                        Inclusive
                      </button>
                      <button
                        type="button"
                        onClick={() => setVatMode('exclusive')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          vatMode === 'exclusive' 
                            ? 'bg-background text-foreground shadow-sm' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="vat-mode-exclusive"
                        aria-pressed={vatMode === 'exclusive'}
                      >
                        Exclusive
                      </button>
                    </div>
                    )}
                  </div>
                  <div className="group relative">
                    <AlertCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 w-56 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-50 border border-border">
                      {isFromBankFeed ? (
                        <>Bank amount already includes VAT, so VAT is <strong>inclusive</strong> here.</>
                      ) : (
                        <><strong>Inclusive:</strong> Amounts entered include VAT. System back-calculates face value.<br/>
                        <strong>Exclusive:</strong> Amounts entered are face value. VAT is added on top.</>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Face Value (Expense Amount) */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Face Value (Expense)</span>
                  <span className="font-medium">{getLocalCurrencySymbol()}{formatDisplayNumber(calculateSubtotal())}</span>
                </div>
                
                {/* Input VAT */}
                {calculateTotalTax() > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Input VAT (Recoverable)</span>
                    <span className="font-medium text-[#108981]">+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotalTax())}</span>
                  </div>
                )}
                {calculateSecondaryTax() > 0 && (
                  <div className="flex justify-between items-center text-sm" data-testid="bill-secondary-tax-row">
                    <span className="text-muted-foreground">{secondaryTaxInfo.label} {secondaryTaxInfo.rate}% ({secondaryTaxInfo.region}){secondaryTaxInfo.recoverable ? ' — recoverable' : ' — part of cost'}</span>
                    <span className="font-medium text-[#108981]">+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateSecondaryTax())}</span>
                  </div>
                )}
                
                {/* Gross Amount (AP) */}
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-lg font-medium text-foreground">Gross Amount (AP)</span>
                  <span className="text-2xl font-bold text-[#283F8B]">
                    {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}
                  </span>
                </div>
                
                {/* Smart AI WHT Suggestion Section */}
                {calculateTotal() > 0 && whtRegimeApplies && (
                  <div className="pt-3 mt-2 border-t border-dashed border-amber-300/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-700 flex items-center gap-1.5">
                        <Wand2 className="w-4 h-4" />
                        Withholding Tax (WHT)
                      </span>
                      {fetchingUnifiedTax && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Analyzing...
                        </span>
                      )}
                    </div>
                    
                    {/* Tax engine: why no WHT / reverse charge (same brain as invoices, bank feed & Review) */}
                    {aiWhtSuggestion && !selectedWhtRate && !aiWhtSuggestion.is_applicable && aiWhtSuggestion.reasoning && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700" data-testid="bill-tax-engine-note">
                        <span className="font-medium text-slate-900">Tax engine: no WHT on this bill.</span> {aiWhtSuggestion.reasoning}
                      </div>
                    )}
                    {aiTaxSuggestion?.suggestions?.some((s) => s?.reverse_charge || s?.decision?.reverse_charge) && (
                      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900" data-testid="bill-reverse-charge-note">
                        <span className="font-medium">Reverse charge:</span> foreign supplier, so you account for the VAT yourself (both legs recorded), not paid to the supplier.
                      </div>
                    )}
                    {/* AI WHT Suggestion Display */}
                    {aiWhtSuggestion && !selectedWhtRate && aiWhtSuggestion.is_applicable && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-amber-800">
                              AI Suggests: {aiWhtSuggestion.suggested_wht_rate}% WHT
                            </p>
                            <p className="text-xs text-amber-600 mt-1">
                              {aiWhtSuggestion.reasoning}
                            </p>
                            {aiWhtSuggestion.suggested_entity_type && !formData.vendorEntityType && (
                              <p className="text-xs text-amber-600 mt-1">
                                Vendor type: <span className="font-medium">{aiWhtSuggestion.suggested_entity_type}</span>
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-red-600">
                              -{getLocalCurrencySymbol()}{formatDisplayNumber(aiWhtSuggestion.wht_amount)}
                            </p>
                            <p className="text-xs text-amber-600">
                              {(aiWhtSuggestion.confidence * 100).toFixed(0)}% confidence
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={applyAiWhtRate}
                            className="bg-amber-600 hover:bg-amber-700 text-white gap-1"
                            data-testid="apply-ai-wht-btn"
                          >
                            <Sparkles className="w-3 h-3" />
                            Apply {aiWhtSuggestion.suggested_wht_rate}%
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowWhtOptions(true)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            Choose Different Rate
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              whtDismissedRef.current = true;
                              setSelectedWhtRate(0);
                              setAiWhtSuggestion(null);
                            }}
                            className="text-muted-foreground"
                          >
                            No WHT
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Manual WHT Rate Selection */}
                    {showWhtOptions && (
                      <div className="bg-muted/50 rounded-lg p-3 space-y-3">
                        <p className="text-sm font-medium">Select WHT Rate</p>
                        <div className="grid grid-cols-4 gap-2">
                          {[0, 2.5, 5, 7.5, 10, 15, 20].map(rate => (
                            <button
                              key={rate}
                              type="button"
                              onClick={() => {
                                setSelectedWhtRate(rate);
                                setShowWhtOptions(false);
                                if (rate > 0) {
                                  toast.success(`Applied ${rate}% WHT rate`);
                                }
                              }}
                              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                selectedWhtRate === rate 
                                  ? 'bg-amber-500 text-white border-amber-500'
                                  : 'bg-background hover:bg-amber-50 border-border hover:border-amber-300'
                              }`}
                            >
                              {rate === 0 ? 'N/A' : `${rate}%`}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Selected WHT Rate Display */}
                    {selectedWhtRate !== null && selectedWhtRate > 0 && (
                      <div className="flex justify-between items-center text-sm bg-amber-50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-amber-700 font-medium flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            WHT Deduction ({selectedWhtRate}%)
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowWhtOptions(true)}
                            className="text-xs text-amber-600 hover:text-amber-800 underline"
                          >
                            Change
                          </button>
                        </div>
                        <span className="font-bold text-red-600">
                          -{getLocalCurrencySymbol()}{formatDisplayNumber(calculateWhtAmount())}
                        </span>
                      </div>
                    )}
                    
                    {/* No WHT Selected */}
                    {selectedWhtRate === 0 && (
                      <div className="flex justify-between items-center text-sm text-muted-foreground p-2">
                        <span>No WHT applicable</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWhtRate(null);
                            fetchUnifiedTaxSuggestion();
                          }}
                          className="text-xs text-[#108981] hover:underline"
                        >
                          Re-check
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Final Amount Payable */}
                {(selectedWhtRate !== null && selectedWhtRate > 0) && (
                  <div className="flex justify-between items-center pt-3 mt-2 bg-green-50 -mx-4 px-4 py-4 rounded-b-lg border-t border-green-200">
                    <span className="text-green-800 font-semibold">Amount Payable to Vendor</span>
                    <span className="text-2xl font-bold text-green-700">
                      {getLocalCurrencySymbol()}{formatDisplayNumber(calculatePayableAmount())}
                    </span>
                  </div>
                )}
                
                {/* When no WHT is selected, show total as payable */}
                {(selectedWhtRate === null || selectedWhtRate === 0) && calculateTotal() > 0 && (
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-base font-medium text-foreground">Amount Payable</span>
                    <span className="text-xl font-bold text-[#283F8B]">
                      {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bank Account Selection for Payment */}
          <Card className="border-2 border-dashed border-[#283F8B]/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-[#283F8B]" />
                Pay From Bank Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bankAccounts.length > 0 ? (
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    Select Bank Account
                    {billCurrency && billCurrency !== getBaseCurrency() && (
                      <span className="text-xs text-muted-foreground">
                        ({billCurrency} accounts shown first)
                      </span>
                    )}
                  </Label>
                  <Select value={selectedBankId} onValueChange={setSelectedBankId} disabled={isFromBankFeed}>
                    <SelectTrigger data-testid="pay-from-bank-select" className={isFromBankFeed ? 'opacity-70' : ''}>
                      <SelectValue placeholder="Choose which bank to pay from" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Show matching currency accounts first for foreign currency bills */}
                      {billCurrency && billCurrency !== getBaseCurrency() && (
                        <>
                          {bankAccounts.filter(b => b.source !== 'stripe' && b.currency === billCurrency).length > 0 && (
                            <div className="px-2 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50">
                              Recommended ({billCurrency} accounts)
                            </div>
                          )}
                          {bankAccounts.filter(b => b.source !== 'stripe' && b.currency === billCurrency).map(bank => (
                            <SelectItem key={bank.id} value={bank.id}>
                              <span className="flex items-center gap-2">
                                <span className="text-emerald-600">✓</span>
                                {bank.bank_name} - ****{bank.account_number?.slice(-4) || '****'} ({bank.currency})
                                {bank.is_default && <span className="text-xs text-[#108981]">(Default)</span>}
                              </span>
                            </SelectItem>
                          ))}
                          {bankAccounts.filter(b => b.source !== 'stripe' && b.currency !== billCurrency).length > 0 && (
                            <div className="px-2 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 mt-1">
                              Other accounts (will calculate FX gain/loss)
                            </div>
                          )}
                          {bankAccounts.filter(b => b.source !== 'stripe' && b.currency !== billCurrency).map(bank => (
                            <SelectItem key={bank.id} value={bank.id}>
                              <span className="flex items-center gap-2 text-muted-foreground">
                                {bank.bank_name} - ****{bank.account_number?.slice(-4) || '****'} ({bank.currency})
                                {bank.is_default && <span className="text-xs">(Default)</span>}
                              </span>
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {/* Show all accounts normally for base currency bills */}
                      {(!billCurrency || billCurrency === getBaseCurrency()) && bankAccounts.filter(b => b.source !== 'stripe').map(bank => (
                        <SelectItem key={bank.id} value={bank.id}>
                          <span className="flex items-center gap-2">
                            {bank.bank_name} - ****{bank.account_number?.slice(-4) || '****'} ({bank.currency})
                            {bank.is_default && <span className="text-xs text-[#108981]">(Default)</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Payment Info */}
                  <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <ExternalLink className="w-4 h-4 mt-0.5 text-[#108981]" />
                      <span>
                        "Save & Pay" opens your bank to finish the payment
                      </span>
                    </div>
                  </div>
                  {/* Add another bank account button */}
                  <div className="pt-4 border-t border-border">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowBankModal(true)}
                      className="gap-2 text-muted-foreground"
                    >
                      <Plus className="w-4 h-4" />
                      Add another bank account
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-3">
                    Add a bank account to use "Save & Pay" and track where payments come from.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBankModal(true)}
                    className="gap-2"
                    data-testid="add-bank-modal-btn"
                  >
                    <Building className="w-4 h-4" />
                    Add Bank Account
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bank Account Modal */}
          <AddBankAccountModal 
            open={showBankModal} 
            onOpenChange={setShowBankModal}
            onSuccess={handleBankAdded}
          />

          {/* MANDATORY Bank Account Modal - Blocks user until bank account is created */}
          <Dialog open={showForceBankModal && !taxGateOpen} onOpenChange={(open) => { if (!open) { setShowForceBankModal(false); navigate(-1); } }}>
            <DialogContent className="sm:max-w-[500px]" onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="w-5 h-5" />
                  Bank Account Required
                </DialogTitle>
                <DialogDescription className="text-base">
                  Add a bank account first — bills and payments need one to come from.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <h4 className="font-medium text-amber-800 mb-2">Why is this required?</h4>
                    <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                      <li>Track which accounts payments are made from</li>
                      <li>Reconcile bank statements with your records</li>
                      <li>Generate accurate cash flow reports</li>
                    </ul>
                  </div>
                <Button
                  className="w-full bg-[#108981] hover:bg-[#108981]/90"
                  onClick={() => {
                    navigate('/settings?tab=banking');
                  }}
                  data-testid="add-bank-required-btn"
                >
                  <Building className="w-4 h-4 mr-2" />
                  Go to Settings &gt; Banking
                </Button>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => navigate(-1)}
                  data-testid="go-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go Back
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bill Date & Due Date */}
          <Card className={financeLockClass} inert={financeLocked || undefined} data-testid="bill-dates-card">
            <CardHeader>
              <CardTitle className="text-lg">Bill Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4" data-testid="bill-project-row">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Project (optional)</Label>
                <ProjectSelect value={billProjectId} onChange={setBillProjectId} createdFrom="bill" />
                <span className="text-xs text-muted-foreground hidden md:inline">Applies to the whole bill: you can override per line item.</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Bill Date */}
                <div className="space-y-2">
                  <Label>Bill Date {isFromBankFeed && <span className="text-xs text-amber-600 ml-1">(Locked)</span>}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full h-12 justify-start text-left font-normal ${isFromBankFeed ? 'opacity-70 cursor-not-allowed' : ''}`}
                        data-testid="bill-date-btn"
                        disabled={isFromBankFeed}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(billDate, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={billDate}
                        onSelect={(date) => date && setBillDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    {isFromBankFeed ? 'Locked to bank transaction date' : 'Date on the bill/invoice'}
                  </p>
                </div>
                
                {/* Due Date */}
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal"
                        data-testid="due-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dueDate, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dueDate}
                        onSelect={(date) => date && setDueDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Payment due in {Math.ceil((dueDate - billDate) / (1000 * 60 * 60 * 24))} days
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description - AI Assisted */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                Description
                {suggestedDescription && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1 font-normal">
                    <Sparkles className="w-3 h-3 text-[#5FC6C5]" />
                    AI-suggested
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter a description for this payment..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="input-description"
              />
              {suggestedDescription && !formData.description && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-[#108981]"
                  onClick={() => {
                    let desc = suggestedDescription;
                    if (formData.vendorName) {
                      desc = desc.replace('{vendor}', formData.vendorName).replace('vendor', formData.vendorName);
                    }
                    setFormData(prev => ({ ...prev, description: desc }));
                  }}
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  Use suggested: "{suggestedDescription.replace('{vendor}', formData.vendorName || 'vendor')}"
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Supporting Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#283F8B]" />
                Supporting Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <input
                type="file"
                ref={docInputRef}
                onChange={handleDocUpload}
                className="hidden"
                accept="image/*,.pdf"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDoc}
                className="w-full gap-2"
              >
                {uploadingDoc ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload Receipt/Invoice
              </Button>
              
              {supportingDocs.length > 0 && (
                <div className="mt-3 space-y-2">
                  {supportingDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span className="text-sm truncate">{doc.filename || doc.document_id}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeDoc(doc.id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card className="bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5">
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground mb-4">
                {isAsset ? (assetType === 'amortizable_prepaid' ? 'Recording a prepaid expense:' : 'Recording an asset purchase:') : 'What would you like to do?'}
              </p>
              
              {isAsset && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  assetType === 'amortizable_prepaid' 
                    ? 'bg-purple-50 border border-purple-200 text-purple-800' 
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
                }`}>
                  <p className="font-medium flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {assetType === 'amortizable_prepaid' ? 'Prepaid Expense Detected' : 'Asset Purchase Detected'}
                  </p>
                  <p className={`mt-1 ${assetType === 'amortizable_prepaid' ? 'text-purple-700' : 'text-amber-700'}`}>
                    This will be recorded as {assetType === 'amortizable_prepaid' ? 'a prepaid asset' : 'a fixed asset'} with monthly {assetType === 'amortizable_prepaid' ? 'amortization' : 'depreciation'} of{' '}
                    <strong>{getLocalCurrencySymbol()}{assetUsefulLife ? formatDisplayNumber((calculateTotal() / parseInt(assetUsefulLife)).toFixed(2)) : '0.00'}</strong>
                  </p>
                </div>
              )}
              
              {/* Notice for prepaid expenses */}
              {isPrepaidExpense && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>Prepaid expense:</strong> paid up front (rent, insurance), so it can't be saved as an unpaid bill.
                  </div>
                </div>
              )}
              
              {/* Bank Feed Completion - Single CTA */}
              {isFromBankFeed ? (
                <div className="space-y-3">
                  {/* Split Mode Banner */}
                  {isSplitMode && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Scissors className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-800">Split Transaction Mode</span>
                      </div>
                      <p className="text-sm text-amber-700 mb-2">
                        Split into lines, each with its own account.
                      </p>
                      <div className="flex items-center justify-between bg-amber-100/50 rounded p-2">
                        <span className="text-sm text-amber-800">Expected Total:</span>
                        <span className="font-bold text-amber-800" data-testid="split-expected-total">
                          {formatCurrency(splitExpectedTotal, billCurrency || getBaseCurrency())}
                          {billCurrency && billCurrency !== getBaseCurrency() && fxRate > 0 && fxRate !== 1 && (
                            <span className="ml-1 text-xs font-normal text-amber-700" data-testid="split-expected-total-base">
                              (≈ {formatCurrency(splitExpectedTotal * fxRate, getBaseCurrency())} @ {Number(fxRate).toLocaleString(undefined, { maximumFractionDigits: 4 })})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-amber-800">Current Total (incl. tax):</span>
                        <span className={`font-bold ${Math.abs(calculateTotal() - splitExpectedTotal) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(calculateTotal(), billCurrency || getBaseCurrency())}
                        </span>
                      </div>
                      {Math.abs(calculateTotal() - splitExpectedTotal) >= 0.01 && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Total must equal {formatCurrency(splitExpectedTotal, billCurrency || getBaseCurrency())}. Remaining: {getLocalCurrencySymbol()}{formatDisplayNumber(Math.abs(splitExpectedTotal - calculateTotal()))}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Standard Bank Feed Banner - for "Not Quite Right" mode */}
                  {!isSplitMode && isNotQuiteRightMode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Edit3 className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Reclassify Transaction</span>
                      </div>
                      <p className="text-sm text-blue-700 mb-2">
                        Change the account. The amount stays as on your bank.
                      </p>
                      <div className="flex items-center justify-between bg-blue-100/50 rounded p-2">
                        <span className="text-sm text-blue-800">Locked Amount:</span>
                        <span className="font-bold text-blue-800" data-testid="locked-amount">
                          {formatCurrency(lockedAmount, billCurrency || getBaseCurrency())}
                          {billCurrency && billCurrency !== getBaseCurrency() && fxRate > 0 && fxRate !== 1 && (
                            <span className="ml-1 text-xs font-normal text-blue-700">(≈ {formatCurrency(lockedAmount * fxRate, getBaseCurrency())})</span>
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" />
                        To split, go back and choose "Split Transaction".
                      </p>
                    </div>
                  )}
                  
                  {/* Standard Bank Feed Banner - regular mode without NQR */}
                  {!isSplitMode && !isNotQuiteRightMode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <strong>From your bank feed:</strong> saving moves it from "Review Needed" to "In Books".
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/bank-feeds?tab=transactions&filter=pending')}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="bg-[#108981] hover:bg-[#108981]/90 px-8"
                      onClick={(e) => handleSubmit(e, 'mark_paid')}
                      disabled={loading || (isSplitMode && Math.abs(calculateTotal() - splitExpectedTotal) >= 0.01)}
                      data-testid="save-complete-transaction-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : isSplitMode ? (
                        <Scissors className="w-5 h-5 mr-2" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                      )}
                      {isSplitMode ? 'Save Split & Complete Transaction' : 'Save & Complete Transaction'}
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Save as Draft - hidden in edit-mode once bill has been approved/paid */}
                {(!isEditMode || existingBill?.status === 'draft') && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 flex flex-col items-center justify-center gap-1 border-2 border-dashed hover:border-gray-400 hover:bg-gray-50"
                  onClick={(e) => handleSubmit(e, 'draft')}
                  disabled={loading}
                  data-testid="save-draft-btn"
                >
                  {loading && saveAction === 'draft' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Clock className="w-5 h-5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-600">Save Draft</span>
                    </>
                  )}
                </Button>
                )}

                {/* Save & Approve (posts journal entry) - NOT available for prepaid expenses */}
                {!isPrepaidExpense && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-14 flex flex-col items-center justify-center gap-1 border-2 hover:border-[#283F8B] hover:bg-[#283F8B]/5"
                    onClick={(e) => handleSubmit(e, 'save')}
                    disabled={loading}
                    data-testid="save-btn"
                  >
                    {loading && saveAction === 'save' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5 text-[#283F8B]" />
                        <span className="text-sm font-medium">Save & Approve</span>
                      </>
                    )}
                  </Button>
                )}

                {/* Save & Pay - Show when bank is selected */}
                {selectedBankId && (
                  <Button
                    type="button"
                    className="h-14 flex flex-col items-center justify-center gap-1 bg-[#283F8B] hover:bg-[#283F8B]/90"
                    onClick={(e) => handleSubmit(e, 'pay')}
                    disabled={loading}
                    data-testid="save-pay-btn"
                  >
                    {loading && saveAction === 'pay' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        <span className="text-sm font-medium">Save & Pay</span>
                      </>
                    )}
                  </Button>
                )}

                {/* iter1421 · Unified — replaces the separate
                    "Save & Mark Paid" and "Save & Part Payment"
                    buttons. Saves the bill first, then routes to the
                    detail page where the unified RecordSettlementModal
                    opens automatically (via ?openPayment=1). */}
                {(!isEditMode || !(existingBill?.amount_paid > 0 || existingBill?.status === 'paid')) && (
                <Button
                  type="button"
                  className={`h-14 flex flex-col items-center justify-center gap-1 px-2 ${
                    isPrepaidExpense 
                      ? 'bg-[#283F8B] hover:bg-[#283F8B]/90 ring-2 ring-[#283F8B]/20' 
                      : 'bg-[#108981] hover:bg-[#108981]/90'
                  }`}
                  onClick={() => {
                    // Save-then-open-unified-modal flow. Keeps a
                    // single CTA that covers Full, Partial and WHT.
                    setPendingPaymentAction('open_unified_after_save');
                    const today = new Date();
                    const bd = billDate ? new Date(billDate.getFullYear(), billDate.getMonth(), billDate.getDate()) : null;
                    setConfirmedPaymentDate(bd && today < bd ? bd : today);
                    setShowPaymentDateDialog(true);
                  }}
                  disabled={loading}
                  data-testid="save-mark-paid-btn"
                >
                  {loading && saveAction === 'mark_paid' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-xs sm:text-sm font-medium text-center leading-tight whitespace-normal">Save & Record Payment</span>
                    </>
                  )}
                </Button>
                )}
              </div>
              )}
            </CardContent>
          </Card>

          {/* Cancel - only show if NOT from bank feed (bank feed has its own cancel button) */}
          {!isFromBankFeed && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/bills')}
              className="text-muted-foreground"
            >
              Cancel
            </Button>
          </div>
          )}
        </form>
      </div>

      {/* Asset Details Dialog */}
      {(() => {
        // iter 817 Phase 8 - Lock useful_life from policy when (a) this is a
        // fixed asset (NOT a prepaid - coverage is bill-driven per Decision 10)
        // AND (b) we know the asset category (typically from the Asset
        // Schedule preset URL).  If both conditions hold, render a locked
        // banner + disabled buttons/input.
        const lockedLife = (
          assetType !== 'amortizable_prepaid' &&
          assetCategoryFromUrl &&
          recognitionPolicy?.depreciation?.useful_life_locked?.[assetCategoryFromUrl]
        );
        return (
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className={`w-5 h-5 ${assetType === 'amortizable_prepaid' ? 'text-purple-500' : 'text-amber-500'}`} />
              {assetType === 'amortizable_prepaid' ? 'Prepaid Expense Details' : 'Asset Details'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {assetType === 'amortizable_prepaid' 
                ? 'This appears to be a prepaid expense. Please specify the period this payment covers for proper amortization.'
                : 'This appears to be an asset purchase. Please specify how long you expect to use this asset for proper depreciation.'
              }
            </p>
            
            <div className="space-y-3">
              <Label>{assetType === 'amortizable_prepaid' ? 'Coverage Period *' : 'Expected Useful Life *'}</Label>

              {/* iter 817 Phase 8 - Lock banner */}
              {lockedLife && (
                <div
                  className="flex items-center justify-between p-3 rounded-lg border border-[#108981]/20 bg-[#108981]/5"
                  data-testid="bill-useful-life-locked-banner"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#108981]/10 flex items-center justify-center shrink-0">
                      <Lock className="w-4 h-4 text-[#108981]" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium">
                        Useful life locked: {lockedLife} months
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Set by company policy for{' '}
                        {assetCategoryLabelFromUrl || assetCategoryFromUrl}.
                        All assets in this category share the same useful life.
                      </p>
                    </div>
                  </div>
                  <a
                    href="/settings/recognition-policy"
                    className="text-xs text-[#108981] hover:underline shrink-0 ml-3"
                    data-testid="bill-edit-recognition-policy-link"
                  >
                    Edit policy →
                  </a>
                </div>
              )}

              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2">
                {[
                  { months: 12, label: '1 Year' },
                  { months: 24, label: '2 Years' },
                  { months: 36, label: '3 Years' },
                  { months: 60, label: '5 Years' },
                ].map(opt => (
                  <Button
                    key={opt.months}
                    type="button"
                    variant={assetUsefulLife === opt.months.toString() ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAssetUsefulLife(opt.months.toString())}
                    disabled={!!lockedLife}
                    className={assetUsefulLife === opt.months.toString() 
                      ? (assetType === 'amortizable_prepaid' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#108981] hover:bg-[#108981]/90')
                      : ''
                    }
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              
              {/* Custom Period Input */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Or enter custom period</Label>
                  <div className="flex gap-2 mt-1">
                    <FormattedNumberInput
                      placeholder="Months"
                      value={assetUsefulLife}
                      onChange={(val) => setAssetUsefulLife(val)}
                      disabled={!!lockedLife}
                      className="h-10"
                      allowDecimals={false}
                      min={1}
                      max={360}
                      data-testid="custom-months-input"
                    />
                    <span className="text-sm text-muted-foreground self-center">months</span>
                  </div>
                </div>
              </div>
              
              {/* Period in years helper */}
              {assetUsefulLife && parseInt(assetUsefulLife) >= 12 && (
                <p className="text-xs text-muted-foreground">
                  = {Math.floor(parseInt(assetUsefulLife) / 12)} year(s) {parseInt(assetUsefulLife) % 12 > 0 ? `and ${parseInt(assetUsefulLife) % 12} month(s)` : ''}
                </p>
              )}
            </div>
            
            {/* Start Date Selection - NEW FEATURE */}
            <div className="space-y-3">
              <Label>{assetType === 'amortizable_prepaid' ? 'Amortization Start Date' : 'Depreciation Start Date'}</Label>
              <p className="text-xs text-muted-foreground">
                Choose when to start recording monthly {assetType === 'amortizable_prepaid' ? 'amortization' : 'depreciation'}.
                Entries will be posted on the 25th of each month.
              </p>
              
              {/* Quick Select Options */}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={depreciationStartOption === 'this_month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setDepreciationStartOption('this_month');
                    setCustomStartDate('');
                  }}
                  className={depreciationStartOption === 'this_month' 
                    ? (assetType === 'amortizable_prepaid' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#108981] hover:bg-[#108981]/90')
                    : ''
                  }
                  data-testid="start-this-month-btn"
                >
                  This Month
                </Button>
                <Button
                  type="button"
                  variant={depreciationStartOption === 'next_month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setDepreciationStartOption('next_month');
                    setCustomStartDate('');
                  }}
                  className={depreciationStartOption === 'next_month' 
                    ? (assetType === 'amortizable_prepaid' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#108981] hover:bg-[#108981]/90')
                    : ''
                  }
                  data-testid="start-next-month-btn"
                >
                  Next Month
                </Button>
                <Button
                  type="button"
                  variant={depreciationStartOption === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDepreciationStartOption('custom')}
                  className={depreciationStartOption === 'custom' 
                    ? (assetType === 'amortizable_prepaid' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#108981] hover:bg-[#108981]/90')
                    : ''
                  }
                  data-testid="start-custom-btn"
                >
                  Custom
                </Button>
              </div>
              
              {/* Custom Date Picker */}
              {depreciationStartOption === 'custom' && (
                <div className="mt-2">
                  <Select value={customStartDate} onValueChange={setCustomStartDate}>
                    <SelectTrigger data-testid="custom-start-date-select">
                      <SelectValue placeholder="Select month..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getMonthOptions().map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* 15th Rule Notice */}
              {depreciationStartOption === 'this_month' && new Date().getDate() > 15 && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <p className="font-medium">Note: Auto-adjusted to next month</p>
                  <p>Since today is after the 15th, {assetType === 'amortizable_prepaid' ? 'amortization' : 'depreciation'} will start next month.</p>
                </div>
              )}
              
              {/* Start Date Display */}
              <p className="text-sm text-muted-foreground">
                Start date: <span className="font-medium text-foreground">{getStartDateDisplay()}</span>
              </p>
            </div>
            
            {assetType !== 'amortizable_prepaid' && (
              <div className="space-y-2">
                <Label>Depreciation Method</Label>
                <Select value={depreciationMethod} onValueChange={setDepreciationMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="straight_line">Straight Line (Equal monthly)</SelectItem>
                    <SelectItem value="reducing_balance">Reducing Balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {assetUsefulLife && calculateTotal() > 0 && parseInt(assetUsefulLife) > 0 && (
              <div className={`p-3 rounded-lg ${assetType === 'amortizable_prepaid' ? 'bg-purple-100' : 'bg-[#108981]/10'}`}>
                <p className={`text-sm font-medium ${assetType === 'amortizable_prepaid' ? 'text-purple-700' : 'text-[#108981]'}`}>
                  Monthly {assetType === 'amortizable_prepaid' ? 'Amortization' : 'Depreciation'}
                </p>
                <p className={`text-2xl font-bold ${assetType === 'amortizable_prepaid' ? 'text-purple-700' : 'text-[#108981]'}`}>
                  {getLocalCurrencySymbol()}{formatDisplayNumber((calculateTotal() / parseInt(assetUsefulLife)).toFixed(2))}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Starting {getStartDateDisplay()} • Entries posted on the 25th of each month
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                // When user cancels, mark asset setup as cancelled
                setAssetSetupCancelled(true);
                setIsAsset(false);
                setAssetUsefulLife('');
                setShowAssetDialog(false);
                toast.info('Asset setup cancelled. This will be recorded as a regular expense.');
              }}
              data-testid="cancel-asset-setup-btn"
            >
              Cancel (No {assetType === 'amortizable_prepaid' ? 'Amortization' : 'Depreciation'})
            </Button>
            <Button 
              onClick={() => {
                if (!assetUsefulLife || parseInt(assetUsefulLife) <= 0) {
                  toast.error(assetType === 'amortizable_prepaid' ? 'Please enter coverage period' : 'Please enter useful life');
                  return;
                }
                // Reset cancelled flag when user confirms
                setAssetSetupCancelled(false);
                setShowAssetDialog(false);
                const startMsg = getStartDateDisplay();
                toast.success(`${assetType === 'amortizable_prepaid' ? 'Amortization' : 'Depreciation'} setup complete. Starting ${startMsg}`);
              }}
              className={assetType === 'amortizable_prepaid' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#108981] hover:bg-[#108981]/90'}
              data-testid="confirm-asset-setup-btn"
            >
              Apply {assetType === 'amortizable_prepaid' ? 'Amortization' : 'Depreciation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        );
      })()}

      {/* Payment Confirmation Dialog */}
      <Dialog open={showPaymentConfirmDialog} onOpenChange={setShowPaymentConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#108981]" />
              Complete Payment
            </DialogTitle>
            <DialogDescription>
              Bill has been approved and the expense recorded. Now complete the payment via your bank, then confirm below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Payment Summary */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pay to:</span>
                <span className="font-medium">{formData.vendorName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-bold text-lg text-[#108981]">
                  {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">From:</span>
                <span className="font-medium">
                  {bankAccounts.find(b => b.id === selectedBankId)?.bank_name || 'Selected Bank'}
                </span>
              </div>
            </div>

            {/* Ledger Status */}
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="awaiting-payment-status">
              <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Awaiting Payment Confirmation</p>
                <p className="text-amber-700 mt-1">
                  Bill approved and expense recognised. The payment journal will post and the bill will be marked paid when you tap "Payment Complete".
                </p>
              </div>
            </div>

            {/* Bank Action */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Complete the actual payment:</p>
              
              {/* Account Type Selector - only show on desktop when bank has both retail and corporate URLs */}
              {(() => {
                const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
                const bankEntry = findBankInRegistry(selectedBank?.bank_name);
                const hasBothTypes = bankEntry?.web_retail && bankEntry?.web_corporate;
                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                
                return !isMobile && hasBothTypes ? (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg" data-testid="bank-account-type-selector">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Banking type:</span>
                    <div className="flex gap-1 flex-1">
                      <button
                        type="button"
                        onClick={() => setBankAccountType('retail')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                          bankAccountType === 'retail' 
                            ? 'bg-[#108981] text-white' 
                            : 'bg-background border border-border text-muted-foreground hover:bg-accent'
                        }`}
                        data-testid="bank-type-retail"
                      >
                        Personal / Retail
                      </button>
                      <button
                        type="button"
                        onClick={() => setBankAccountType('corporate')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                          bankAccountType === 'corporate' 
                            ? 'bg-[#283F8B] text-white' 
                            : 'bg-background border border-border text-muted-foreground hover:bg-accent'
                        }`}
                        data-testid="bank-type-corporate"
                      >
                        Corporate / Business
                      </button>
                    </div>
                  </div>
                ) : null;
              })()}
              
              <Button 
                onClick={handleOpenBankForPayment}
                className="w-full gap-2 bg-[#283F8B] hover:bg-[#283F8B]/90"
                disabled={paymentInProgress}
                data-testid="open-bank-app-button"
              >
                <ExternalLink className="w-4 h-4" />
                {(() => {
                  const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
                  const redirect = getBankRedirectUrl(selectedBank?.bank_name);
                  if (!redirect) return `Open ${selectedBank?.bank_name || 'Bank'}`;
                  if (redirect.type === 'android_app' || redirect.type === 'ios_app') return `Open ${selectedBank?.bank_name} App`;
                  return `Open ${selectedBank?.bank_name} Internet Banking`;
                })()}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Transfer {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())} to {formData.vendorName}
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPaymentConfirmDialog(false);
                toast.info('Bill saved as awaiting payment', {
                  description: 'Record the payment from the bill page when it\'s done.'
                });
                navigate('/bills');
              }}
              className="w-full sm:w-auto"
              data-testid="pay-later-btn"
            >
              I'll Pay Later
            </Button>
            <Button 
              onClick={handlePaymentComplete}
              disabled={completingPayment}
              className="w-full sm:w-auto gap-2 bg-[#108981] hover:bg-[#108981]/90"
              data-testid="payment-complete-btn"
            >
              {completingPayment ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Payment Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={showPartialPaymentDialog} onOpenChange={setShowPartialPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Save the bill and record a partial payment in one step.
              Remaining balance: {getLocalCurrencySymbol()}{formatDisplayNumber((parseFloat(formData.amount) || 0) - (parseFloat(partialPaymentAmount) || 0))}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bill Total</Label>
              <p className="text-2xl font-bold text-foreground">
                {getLocalCurrencySymbol()}{formatDisplayNumber(formData.amount || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bill-partial-amount">Payment Amount Made</Label>
              <CurrencyInput
                id="bill-partial-amount"
                value={partialPaymentAmount}
                onChange={setPartialPaymentAmount}
                placeholder="0.00"
                currencySymbol={getLocalCurrencySymbol()}
                data-testid="partial-payment-amount"
              />
              {parseFloat(partialPaymentAmount) > parseFloat(formData.amount || 0) && (
                <p className="text-sm text-red-500">Payment cannot exceed bill total</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="bill-partial-ref">Payment Reference (Optional)</Label>
              <Input
                id="bill-partial-ref"
                value={partialPaymentRef}
                onChange={(e) => setPartialPaymentRef(e.target.value)}
                placeholder="e.g., Bank transfer, Cheque #123"
                data-testid="partial-payment-ref"
              />
            </div>
            {/* iter971k-Z · Turn 5c - Payment Date picker (defaults to today, user editable). */}
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-11"
                    data-testid="partial-payment-date-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 opacity-60" />
                    {partialPaymentDate ? format(partialPaymentDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={partialPaymentDate}
                    onSelect={(d) => d && setPartialPaymentDate(d)}
                    /* iter971k-Z · Turn 5d - payment date >= bill date */
                    disabled={(d) => billDate && d < new Date(billDate.getFullYear(), billDate.getMonth(), billDate.getDate())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">
                Used for GL posting and appears on the payment record.
                {billDate ? ` Cannot be earlier than the bill date (${format(billDate, 'PPP')}).` : ''}
              </p>
            </div>
            {bankAccounts.length > 0 && (
              <div className="space-y-2">
                <Label>Pay from Bank Account</Label>
                <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Show matching currency accounts first */}
                    {billCurrency && billCurrency !== getBaseCurrency() && bankAccounts.filter(b => b.source !== 'stripe' && b.currency === billCurrency).length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50">
                          Recommended ({billCurrency})
                        </div>
                        {bankAccounts.filter(b => b.source !== 'stripe' && b.currency === billCurrency).map(bank => (
                          <SelectItem key={bank.id} value={bank.id}>
                            <span className="text-emerald-600 mr-1">✓</span>
                            {bank.account_name} ({bank.currency})
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-1">
                          Other accounts
                        </div>
                        {bankAccounts.filter(b => b.source !== 'stripe' && b.currency !== billCurrency).map(bank => (
                          <SelectItem key={bank.id} value={bank.id}>
                            {bank.account_name} ({bank.currency})
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {(!billCurrency || billCurrency === getBaseCurrency()) && bankAccounts.filter(b => b.source !== 'stripe').map(bank => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.account_name} ({bank.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPartialPaymentDialog(false);
                setPartialPaymentAmount('');
                setPartialPaymentRef('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                setShowPartialPaymentDialog(false);
                handleSubmit(e, 'partial_payment');
              }}
              disabled={
                !partialPaymentAmount || 
                parseFloat(partialPaymentAmount) <= 0 || 
                parseFloat(partialPaymentAmount) > parseFloat(formData.amount || 0) ||
                loading
              }
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-partial-payment"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save & Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter971k-Z · Turn 5c - Payment Date Confirm Dialog (Mark as Paid) */}
      <Dialog open={showPaymentDateDialog} onOpenChange={setShowPaymentDateDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="payment-date-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm payment date</DialogTitle>
            <DialogDescription>
              This is the date used for the GL journal posting. Defaults to today.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Payment Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-11 mt-2"
                  data-testid="mark-paid-date-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 opacity-60" />
                  {confirmedPaymentDate ? format(confirmedPaymentDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={confirmedPaymentDate}
                  onSelect={(d) => d && setConfirmedPaymentDate(d)}
                  disabled={(d) => billDate && d < new Date(billDate.getFullYear(), billDate.getMonth(), billDate.getDate())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {billDate && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Cannot be earlier than the bill date ({format(billDate, 'PPP')}).
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDateDialog(false)}>Cancel</Button>
            <Button
              className="bg-[#108981] hover:bg-[#108981]/90"
              onClick={(e) => {
                setShowPaymentDateDialog(false);
                const action = pendingPaymentAction || 'mark_paid';
                setPendingPaymentAction(null);
                // iter1421 · When the user picked the unified
                // "Save & Record Payment" flow, save the bill first
                // then hand off to the detail page's unified modal.
                if (action === 'open_unified_after_save') {
                  handleSubmit(e, 'open_unified_after_save');
                } else {
                  handleSubmit(e, action);
                }
              }}
              data-testid="confirm-payment-date-btn"
            >
              Confirm & Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Special Item Modal - For Fixed Assets, Prepaid Expenses, Investments, Loans, Deferred Revenue */}
      <SpecialItemModal
        open={showSpecialItemModal}
        onOpenChange={setShowSpecialItemModal}
        itemType={specialItemType}
        lineItem={lineItems.find(item => item.id === currentSpecialItemLineId)}
        amount={parseFloat(lineItems.find(item => item.id === currentSpecialItemLineId)?.amount || 0)}
        currencySymbol={getLocalCurrencySymbol()}
        formatNumber={formatDisplayNumber}
        counterpartyName={formData.vendorName}
        documentType="bill"
        glCode={lineItems.find(item => item.id === currentSpecialItemLineId)?.glCode || ''}
        onSave={(lineItemId, terms) => {
          // Store the terms for this line item
          setSpecialItemTerms(prev => ({
            ...prev,
            [lineItemId]: terms
          }));
          
          // Update the line item to mark it as having special terms
          updateLineItem(lineItemId, 'specialTerms', terms);
          
          // Check if there are more items in the queue
          if (specialItemQueue.length > 0) {
            const [nextItem, ...remainingQueue] = specialItemQueue;
            setSpecialItemQueue(remainingQueue);
            setCurrentSpecialItemLineId(nextItem.lineItemId);
            setSpecialItemType(nextItem.itemType);
          } else {
            setShowSpecialItemModal(false);
          }
        }}
        onCancel={(lineItemId) => {
          // User skipped - mark as no schedule
          updateLineItem(lineItemId, 'specialTerms', { skipped: true });
          
          // Process next in queue
          if (specialItemQueue.length > 0) {
            const [nextItem, ...remainingQueue] = specialItemQueue;
            setSpecialItemQueue(remainingQueue);
            setCurrentSpecialItemLineId(nextItem.lineItemId);
            setSpecialItemType(nextItem.itemType);
          } else {
            setShowSpecialItemModal(false);
          }
        }}
      />
      
      {/* Accounting Start Date Modal */}
      <AccountingStartDateModal 
        open={showStartDateModal} 
        onClose={() => setShowStartDateModal(false)}
        transactionType="bill"
      />

      {/* Period Lock Warning Dialog */}
      <PeriodLockWarningDialog
        open={periodLockDialog.open}
        onClose={clearPeriodLock}
        transactionDate={periodLockDialog.transactionDate}
        lockedPeriod={periodLockDialog.lockedPeriod}
        onPostToOriginalDate={periodLockDialog.onPostToOriginalDate}
        onPostToToday={periodLockDialog.onPostToToday}
      />

      {/* Create New Account Modal (from GL dropdown) */}
      <NewAccountModal
        open={showCreateAccountModal}
        onOpenChange={setShowCreateAccountModal}
        existingAccounts={coaAccounts}
        onAccountCreated={(newAcc) => {
          setCoaAccounts(prev => [...prev, newAcc].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
        }}
      />
      {/* iter971k-Z · Turn 4 - Inline Edit Contact modal (linked vendor) */}
      {selectedVendorId && (
        <ContactEditModal
          open={contactEditOpen}
          onOpenChange={setContactEditOpen}
          contactId={selectedVendorId}
          onSaved={(updated) => {
            setFormData((prev) => ({
              ...prev,
              vendorName: updated.name || prev.vendorName,
              vendorEntityType: updated.entity_type || prev.vendorEntityType,
            }));
          }}
        />
      )}
    </>
  );

  return embedded ? pageBody : <DashboardLayout>{pageBody}</DashboardLayout>;
};

export default CreateBillPage;
